import { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  Bell,
  Check,
  CheckCircle2,
  ChevronRight,
  Clock,
  Download,
  FileText,
  Headset,
  ImagePlus,
  List,
  Mail,
  MapPin,
  Navigation,
  Phone,
  Plus,
  Search,
  Shield,
  Star,
  User,
  Users,
  Wrench,
  X,
  Zap,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { downloadCskTicketPdf } from '../utils/downloadCskTicketPdf';

const ROLES = ['Client', 'Admin', 'Engineer'];

const ROLE_CONFIG = {
  Client: {
    label: 'Client View',
    title: 'Customer portal',
    description: 'Open maintenance or installation tickets, track progress, update site coordinates, and confirm service.',
    icon: User,
    accent: 'border-sky-200 bg-sky-50 text-sky-900',
    iconWrap: 'bg-sky-600 text-white',
    tabActive: 'bg-white text-sky-700 shadow-sm',
  },
  Admin: {
    label: 'Admin View',
    title: 'Operations control',
    description: 'Review tickets, assign engineers, track materials reconciliation, and monitor SLA.',
    icon: Shield,
    accent: 'border-violet-200 bg-violet-50 text-violet-900',
    iconWrap: 'bg-violet-600 text-white',
    tabActive: 'bg-white text-violet-700 shadow-sm',
  },
  Engineer: {
    label: 'Engineer View',
    title: 'Field work view',
    description: 'Select tools & materials, reconcile usage after the job, and optionally attach site photos.',
    icon: Wrench,
    accent: 'border-teal-200 bg-teal-50 text-teal-900',
    iconWrap: 'bg-teal-600 text-white',
    tabActive: 'bg-white text-teal-700 shadow-sm',
  },
};

const PROGRESS_STEPS = [
  { key: 'submitted', label: 'Submitted' },
  { key: 'review', label: 'Under review' },
  { key: 'assigned', label: 'Engineer assigned' },
  { key: 'in_progress', label: 'Work in progress' },
  { key: 'resolved', label: 'Resolved' },
];

const WORK_ORDER_STEPS = [
  { key: 'assigned', label: 'Assigned' },
  { key: 'tools', label: 'Tools confirmed' },
  { key: 'in_progress', label: 'In progress' },
  { key: 'reconciled', label: 'Reconciled' },
  { key: 'closed', label: 'Completed' },
];

const FIELD_TOOLS = [
  { id: 'otdr', label: 'OTDR / fiber tester', kind: 'tool' },
  { id: 'splice-kit', label: 'Fiber splice kit', kind: 'tool' },
  { id: 'power-meter', label: 'Optical power meter', kind: 'tool' },
  { id: 'cpe-toolkit', label: 'CPE installation toolkit', kind: 'tool' },
  { id: 'fusion-splicer', label: 'Fusion splicer', kind: 'tool' },
  { id: 'ladder', label: 'Extension ladder', kind: 'tool' },
  { id: 'safety-gear', label: 'Safety harness & PPE', kind: 'tool' },
  { id: 'cable-tools', label: 'Cable stripping tools', kind: 'tool' },
  { id: 'fat-8p-splitter', label: 'FAT 8p with splitter', kind: 'material' },
  { id: 'fo-aerial-8c', label: 'FO Aerial Cable 8c', kind: 'material' },
  { id: 'fo-aerial-12c', label: 'FO Aerial Cable 12c', kind: 'material' },
  { id: 'fo-aerial-24c', label: 'FO Aerial Cable 24c', kind: 'material' },
  { id: 'splice-enclosure-12c', label: 'Splice enclosure 12c', kind: 'material' },
  { id: 'splice-enclosure-24c', label: 'Splice enclosure 24c', kind: 'material' },
  { id: 'splice-enclosure-48c', label: 'Splice enclosure 48c', kind: 'material' },
  { id: 'patch-cord-sc-apc', label: 'Patch cord 3m SC/APC', kind: 'material' },
  { id: 'atb', label: 'ATB', kind: 'material' },
  { id: 'drop-cable-2c', label: 'Drop cable 2c', kind: 'material' },
];

const FIELD_TOOL_BY_ID = Object.fromEntries(FIELD_TOOLS.map((t) => [t.id, t]));

const getWorkOrderMaterialPlan = (workOrder) => {
  if (Array.isArray(workOrder?.materialPlan) && workOrder.materialPlan.length) {
    return workOrder.materialPlan;
  }
  return (workOrder?.selectedTools || []).map((label) => ({
    id: String(label).toLowerCase().replace(/[^\w]+/g, '-'),
    label: String(label),
    plannedQty: 1,
    kind: 'tool',
  }));
};

const formatMaterialQtyLine = (item, qtyKey = 'plannedQty') => {
  const qty = Number(item?.[qtyKey]) || 1;
  return qty > 1 ? `${item.label} × ${qty}` : item.label;
};

const readImageFilesAsDataUrls = (files, max = 2) =>
  Promise.all(
    Array.from(files)
      .slice(0, max)
      .filter((f) => f.type.startsWith('image/'))
      .map(
        (file) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () =>
              resolve({ name: file.name, dataUrl: reader.result, uploadedAt: new Date().toISOString() });
            reader.onerror = reject;
            reader.readAsDataURL(file);
          })
      )
  );

const getWorkOrderProgressIndex = (workOrder) => {
  if (workOrder.status === 'Closed') return 4;
  if (workOrder.materialsReconciled) return 3;
  if (workOrder.status === 'In Progress') return 2;
  if (workOrder.workConfirmed) return 1;
  return 0;
};

const TICKET_TYPES = [
  { value: 'Maintenance', code: 'TT', label: 'Maintenance (TT)' },
  { value: 'Installation', code: 'WO', label: 'Installation (WO)' },
];

const ticketTypeMeta = (ticket) => {
  const found = TICKET_TYPES.find(
    (t) => t.value === ticket?.ticketType || t.code === ticket?.typeCode
  );
  if (found) return found;
  if (String(ticket?.id || '').startsWith('WO-')) {
    return TICKET_TYPES.find((t) => t.code === 'WO');
  }
  return TICKET_TYPES.find((t) => t.code === 'TT');
};

const formatTicketCoordinates = (ticket) => {
  if (ticket?.coordinatesLabel) return ticket.coordinatesLabel;
  const lat = ticket?.coordinates?.latitude ?? ticket?.latitude;
  const lng = ticket?.coordinates?.longitude ?? ticket?.longitude;
  if (lat !== undefined && lat !== '' && lng !== undefined && lng !== '') {
    return `${lat}, ${lng}`;
  }
  if (lat !== undefined && lat !== '') return String(lat);
  return '';
};

/** Accept any coordinate text — decimal pair, plus code, map link, landmark, etc. */
const buildCoordinatesFromInput = (input) => {
  const text = String(input || '').trim();
  if (!text) return null;
  const commaMatch = text.match(/^([^,]+),\s*(.+)$/);
  if (commaMatch) {
    const latitude = commaMatch[1].trim();
    const longitude = commaMatch[2].trim();
    return {
      coordinatesLabel: text,
      coordinates: { latitude, longitude },
      latitude,
      longitude,
    };
  }
  return {
    coordinatesLabel: text,
    coordinates: { latitude: text, longitude: '' },
    latitude: text,
    longitude: '',
  };
};

/** Google Maps directions URL for coordinates, address, plus code, or map link. */
const buildMapsDirectionsUrl = (destination) => {
  const text = String(destination || '').trim();
  if (!text) return null;
  if (/^https?:\/\//i.test(text)) return text;
  const coordMatch = text.match(/^(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)/);
  if (coordMatch) {
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
      `${coordMatch[1]},${coordMatch[2]}`
    )}`;
  }
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(text)}`;
};

const getTicketDirectionsUrl = (ticket, destinationOverride) => {
  if (destinationOverride) return buildMapsDirectionsUrl(destinationOverride);
  if (!ticket) return null;
  const coords = formatTicketCoordinates(ticket);
  return buildMapsDirectionsUrl(coords || ticket.location);
};

function TicketDirectionsLink({
  ticket,
  destination,
  label,
  className = '',
  stopPropagation = true,
  showIcon = true,
}) {
  const display =
    label ||
    (destination ? String(destination).trim() : '') ||
    formatTicketCoordinates(ticket) ||
    ticket?.location ||
    '';
  const url = getTicketDirectionsUrl(ticket, destination);
  if (!url || !display) {
    return label ? <span className={className}>{label}</span> : null;
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      title="Open in Google Maps for directions"
      className={`inline-flex items-center gap-1 text-sky-600 underline-offset-2 transition hover:text-sky-800 hover:underline ${className}`}
      onClick={(e) => {
        if (stopPropagation) e.stopPropagation();
      }}
    >
      {showIcon ? <Navigation className="h-3.5 w-3.5 shrink-0" aria-hidden /> : null}
      <span>{display}</span>
    </a>
  );
}

const STATUS_STYLES = {
  Open: 'bg-sky-100 text-sky-800',
  'In Progress': 'bg-amber-100 text-amber-800',
  Resolved: 'bg-emerald-100 text-emerald-800',
  Pending: 'bg-slate-100 text-slate-700',
  Assigned: 'bg-teal-100 text-teal-800',
  Closed: 'bg-slate-200 text-slate-600',
};

/** Maintenance (TT) — priority SLA levels. */
const TICKET_PRIORITIES = [
  { value: 'Critical', sla: '3 hours' },
  { value: 'Major', sla: '3 hours' },
  { value: 'Minor', sla: '24 hours' },
];

const PRIORITY_SLA = Object.fromEntries(TICKET_PRIORITIES.map((p) => [p.value, p.sla]));
const PRIORITY_SLA_HOURS = { Critical: 3, Major: 3, Minor: 24 };

const formatPriorityLabel = (priority) => {
  const sla = PRIORITY_SLA[priority];
  return sla ? `${priority} (${sla})` : priority;
};

const PRIORITY_STYLES = {
  Critical: 'bg-rose-100 text-rose-800',
  Major: 'bg-amber-100 text-amber-800',
  Minor: 'bg-sky-100 text-sky-800',
};

/** Installation (WO) — expected resolution timeline. */
const TICKET_TIMELINES = [
  { value: 'At timeline', hours: 3 },
  { value: '24 HR', hours: 24 },
  { value: '7 Days', hours: 168 },
  { value: '1 Week', hours: 168 },
  { value: '2 Weeks', hours: 336 },
];

const LEGACY_PRIORITY_TO_TIMELINE = {
  Critical: 'At timeline',
  Major: '24 HR',
  Minor: '7 Days',
};

const TIMELINE_HOURS = Object.fromEntries(TICKET_TIMELINES.map((t) => [t.value, t.hours]));

const isPriorityValue = (value) => TICKET_PRIORITIES.some((p) => p.value === value);

const normalizeTimelineValue = (value) => LEGACY_PRIORITY_TO_TIMELINE[value] || value;

const formatTimelineLabel = (value) => normalizeTimelineValue(value) || '—';

const formatTicketPriorityLabel = (value) =>
  isPriorityValue(value) ? formatPriorityLabel(value) : formatTimelineLabel(value);

const TIMELINE_STYLES = {
  'At timeline': 'bg-rose-100 text-rose-800',
  '24 HR': 'bg-amber-100 text-amber-800',
  '7 Days': 'bg-sky-100 text-sky-800',
  '1 Week': 'bg-indigo-100 text-indigo-800',
  '2 Weeks': 'bg-slate-100 text-slate-700',
};

const isInstallationTicket = (ticket) =>
  ticketTypeMeta(ticket)?.code === 'WO' || ticket?.typeCode === 'WO';

const CLIENT_ACCOUNT = {
  name: 'Nairobi Fiber Homes',
  accountId: 'ACC-KE-1042',
  plan: 'Business Fiber 100 Mbps',
  contact: '+254 712 000 101',
  address: 'Westlands, Nairobi',
};

const todayIso = () => new Date().toISOString().slice(0, 10);

const formatTimestamp = (iso) => {
  try {
    return new Date(iso).toLocaleString('en-KE', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const getProgressIndex = (ticket) => {
  if (ticket.status === 'Resolved') return 4;
  if (ticket.status === 'In Progress') return ticket.engineer ? 3 : 2;
  return 1;
};

const nextTicketId = (tickets, typeCode = 'TT') => {
  const prefix = typeCode === 'WO' ? 'WO' : 'TT';
  const nums = tickets
    .map((t) => {
      const id = String(t.id || '');
      if (!id.startsWith(`${prefix}-`)) return NaN;
      return parseInt(id.replace(/\D/g, ''), 10);
    })
    .filter((n) => Number.isFinite(n));
  const next = (nums.length ? Math.max(...nums) : 2400) + 1;
  return `${prefix}-${next}`;
};

const workOrderIdFromTicketId = (ticketId) => {
  const num = String(ticketId).replace(/^TT-/i, '').replace(/\D/g, '');
  return num ? `WO-${num}` : `WO-${Date.now()}`;
};

const ticketDisplayRef = (ticket) => ticket.workOrderId || ticket.id;

const getLinkedWorkOrder = (ticket, workOrders) =>
  workOrders.find((w) => w.ticketId === ticket.id || w.id === ticket.workOrderId);

const showTicketAsWorkOrder = (ticket) =>
  Boolean(ticket.workOrderId && (ticket.status === 'In Progress' || ticket.status === 'Resolved'));

/** Field technical team — Sagemcom Kenya field engineers. */
const TECHNICAL_TEAM = [
  {
    id: 'TL-01',
    name: 'Cromwell Oronje',
    gender: 'male',
    rank: 'Team Leader',
    specialty: 'Fiber core & ONT diagnostics',
    zone: 'Nairobi',
    phone: '+254 722 410 101',
    email: 'cromwell-oronje.onyango@sagemcom.com',
  },
  {
    id: 'TL-02',
    name: 'Pancras Okelo',
    gender: 'male',
    rank: 'Team Leader',
    specialty: 'Billing systems & CPE support',
    zone: 'Western',
    phone: '+254 733 420 202',
    email: 'pancras.otieno@sagemcom.com',
  },
  {
    id: 'ENG-03',
    name: 'Maxwel Ougo',
    gender: 'male',
    rank: 'Engineer',
    specialty: 'Last-mile fiber & network performance',
    zone: 'Coast',
    phone: '+254 711 430 303',
    email: 'maxwel.ougo@sagemcom.com',
  },
  {
    id: 'ENG-04',
    name: 'Geoffrey Mwangi Nduati',
    gender: 'male',
    rank: 'Engineer',
    specialty: 'Emergency splice & backbone repair',
    zone: 'Rift Valley',
    phone: '+254 700 440 404',
    email: 'geoffrey.mwangi@sagemcom.com',
  },
  {
    id: 'ENG-05',
    name: 'Lorna Kiambi Kendi',
    gender: 'female',
    rank: 'Engineer',
    specialty: 'CPE installation & customer premises support',
    zone: 'Nairobi',
    phone: '+254 722 450 505',
    email: 'lorna.kendi@sagemcom.com',
  },
  {
    id: 'ENG-06',
    name: 'Mathew Kimolo',
    gender: 'male',
    rank: 'Engineer',
    specialty: 'Network performance & field diagnostics',
    zone: 'Nairobi',
    phone: '+254 733 460 606',
    email: 'mathew.kimolo@sagemcom.com',
  },
];

const TEAM_RANK_ORDER = { 'Team Leader': 0, Engineer: 1, Technician: 2 };

/** Team leaders & engineers available for work order assignment. */
const ENGINEERS = TECHNICAL_TEAM.filter(
  (member) => member.rank === 'Team Leader' || member.rank === 'Engineer'
).map((member) => ({
  name: member.name,
  role: member.rank,
  zone: member.zone,
}));

const getTeamMemberAvailability = (name, workOrders) =>
  workOrders.some((w) => w.engineer === name && w.status !== 'Closed') ? 'Busy' : 'Available';

const getTeamMemberActiveJobs = (name, workOrders) =>
  workOrders.filter((w) => w.engineer === name && w.status !== 'Closed').length;

const getTeamMemberGender = (name) =>
  TECHNICAL_TEAM.find((member) => member.name === name)?.gender || 'male';

const getTeamMemberByName = (name) =>
  TECHNICAL_TEAM.find((member) => member.name === name) || null;

const isActiveWorkOrderStatus = (status) => status === 'Assigned' || status === 'In Progress';

const ENGINEER_DEMO_NAME = 'Cromwell Oronje';

const defaultWorkOrderSchedule = () => {
  const d = new Date();
  d.setHours(d.getHours() + 2, 0, 0, 0);
  return d.toLocaleString('en-KE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const inferZoneFromLocation = (location = '') => {
  const loc = location.toLowerCase();
  if (loc.includes('mombasa') || loc.includes('nyali')) return 'Coast';
  if (loc.includes('kisumu')) return 'Western';
  if (loc.includes('nakuru')) return 'Rift Valley';
  return 'Nairobi';
};

const needsClientConfirmation = (ticket) =>
  ticket.status === 'Resolved' && !ticket.clientConfirmed;

const isPendingClientConfirm = (ticket) =>
  ticket.status === 'Resolved' && !ticket.clientConfirmed;

const isAdminResolved = (ticket) =>
  ticket.status === 'Resolved' && ticket.clientConfirmed;

const isAdminInProgress = (ticket) =>
  ticket.status === 'In Progress' || isPendingClientConfirm(ticket);

const adminTicketStatusLabel = (ticket) => {
  if (isPendingClientConfirm(ticket)) return 'WO · Awaiting client';
  if (isAdminResolved(ticket)) return 'WO · Resolved';
  if (ticket.workOrderId && ticket.status === 'In Progress') return 'WO · In progress';
  if (ticket.workOrderId) return 'Work order';
  return ticket.status;
};

const timelineSlaHours = (value) => {
  const v = String(value || '').trim();
  if (PRIORITY_SLA_HOURS[v] != null) return PRIORITY_SLA_HOURS[v];
  const timeline = normalizeTimelineValue(v);
  return TIMELINE_HOURS[timeline] ?? 24;
};

const parseWorkOrderDate = (value) => {
  if (!value) return null;
  const direct = new Date(value);
  if (!Number.isNaN(direct.getTime())) return direct;
  const normalized = String(value).replace(' ', 'T');
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const getWorkOrderSlaDueAt = (workOrder, ticket) => {
  const start = parseWorkOrderDate(workOrder?.issuedAt || workOrder?.scheduled);
  if (!start) return null;
  const hours = timelineSlaHours(ticket?.priority);
  return new Date(start.getTime() + hours * 60 * 60 * 1000);
};

const isWorkOrderDelayed = (workOrder, ticket) => {
  if (workOrder?.status !== 'Closed' || !workOrder?.completedAt) return false;
  const due = getWorkOrderSlaDueAt(workOrder, ticket);
  const completed = parseWorkOrderDate(workOrder.completedAt);
  if (!due || !completed) return false;
  return completed.getTime() > due.getTime();
};

const formatSlaDateTime = (date) => {
  if (!date) return '—';
  try {
    return date.toLocaleString('en-KE', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
};

const getResolvedCompletedWorkOrders = (workOrders, tickets, { adminConfirmedOnly = false } = {}) =>
  workOrders
    .filter((w) => w.status === 'Closed' && w.completedAt)
    .map((w) => ({ workOrder: w, ticket: tickets.find((t) => t.id === w.ticketId) }))
    .filter(({ ticket }) => {
      if (!ticket) return false;
      if (adminConfirmedOnly) return isAdminResolved(ticket);
      return ticket.status === 'Resolved';
    });

const splitWorkOrdersBySla = (pairs) => {
  const onTime = [];
  const delayed = [];
  pairs.forEach((entry) => {
    if (isWorkOrderDelayed(entry.workOrder, entry.ticket)) delayed.push(entry);
    else onTime.push(entry);
  });
  return { onTime, delayed };
};

const formatExcelDateTime = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-KE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return String(iso);
  }
};

function useCloseOnEscape(onClose) {
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);
}

/** Admin export — all tickets plus a detailed timeline sheet. */
const exportAdminTicketsToExcel = (tickets, workOrders) => {
  const ticketHeaders = [
    'Ticket ID',
    'Ticket Type',
    'Work Order ID',
    'Client',
    'Contact',
    'Location',
    'Coordinates',
    'Category',
    'Issue',
    'Priority / Timeline',
    'SLA / Target',
    'Status',
    'Admin Status',
    'Engineer',
    'Created Date',
    'Client Confirmed',
    'Confirmed At',
    'Service Rating',
    'WO Title',
    'WO Status',
    'WO Zone',
    'WO Scheduled',
    'WO Issued At',
    'WO Completed At',
    'Tools Confirmed',
    'Planned Materials',
    'Materials Reconciled',
    'Site Photos',
    'Delay Reason',
    'SLA Due At',
    'SLA Outcome',
    'Timeline Events',
  ];

  const ticketRows = tickets.map((ticket) => {
    const workOrder = getLinkedWorkOrder(ticket, workOrders);
    const slaDue = workOrder ? getWorkOrderSlaDueAt(workOrder, ticket) : null;
    let slaOutcome = '';
    if (workOrder?.status === 'Closed' && workOrder?.completedAt) {
      slaOutcome = isWorkOrderDelayed(workOrder, ticket) ? 'Delayed' : 'On time';
    }

    return [
      ticket.id,
      ticketTypeMeta(ticket)?.label || ticket.ticketType || '',
      ticket.workOrderId || workOrder?.id || '',
      ticket.client,
      ticket.contact,
      ticket.location,
      formatTicketCoordinates(ticket),
      ticket.category || ticketTypeMeta(ticket)?.value || '',
      ticket.issue,
      formatTicketPriorityLabel(ticket.priority),
      isInstallationTicket(ticket)
        ? formatTimelineLabel(ticket.priority)
        : PRIORITY_SLA[ticket.priority] || '',
      ticket.status,
      adminTicketStatusLabel(ticket),
      ticket.engineer || '',
      ticket.created || '',
      ticket.clientConfirmed ? 'Yes' : 'No',
      ticket.confirmedAt ? formatExcelDateTime(ticket.confirmedAt) : '',
      ticket.serviceRating ?? '',
      workOrder?.title || '',
      workOrder?.status || '',
      workOrder?.zone || '',
      workOrder?.scheduled || '',
      workOrder?.issuedAt ? formatExcelDateTime(workOrder.issuedAt) : '',
      workOrder?.completedAt ? formatExcelDateTime(workOrder.completedAt) : '',
      workOrder?.workConfirmed ? 'Yes' : 'No',
      getWorkOrderMaterialPlan(workOrder)
        .map((m) => formatMaterialQtyLine(m))
        .join('; '),
      (workOrder?.reconciledMaterials || [])
        .map((m) => `${m.label}:${m.usedQty}/${m.plannedQty}`)
        .join('; '),
      workOrder?.sitePhotos?.length ? workOrder.sitePhotos.length : '',
      workOrder?.delayReason || '',
      slaDue ? formatExcelDateTime(slaDue.toISOString()) : '',
      slaOutcome,
      (ticket.updates || []).length,
    ];
  });

  const timelineHeaders = [
    'Ticket ID',
    'Work Order ID',
    'Client',
    'Priority / Timeline',
    'Ticket Status',
    'Event #',
    'Date & Time',
    'Activity',
    'By',
  ];

  const timelineRows = [];
  tickets.forEach((ticket) => {
    const updates = [...(ticket.updates || [])].sort(
      (a, b) => new Date(a.at).getTime() - new Date(b.at).getTime()
    );
    updates.forEach((update, index) => {
      timelineRows.push([
        ticket.id,
        ticket.workOrderId || '',
        ticket.client,
        formatTicketPriorityLabel(ticket.priority),
        ticket.status,
        index + 1,
        formatExcelDateTime(update.at),
        update.message,
        update.by || '',
      ]);
    });
  });

  const wb = XLSX.utils.book_new();
  const ticketsSheet = XLSX.utils.aoa_to_sheet([ticketHeaders, ...ticketRows]);
  const timelineSheet = XLSX.utils.aoa_to_sheet([timelineHeaders, ...timelineRows]);

  ticketsSheet['!cols'] = ticketHeaders.map((header) => ({
    wch: Math.min(48, Math.max(12, header.length + 4)),
  }));
  timelineSheet['!cols'] = [
    { wch: 12 },
    { wch: 14 },
    { wch: 26 },
    { wch: 10 },
    { wch: 14 },
    { wch: 8 },
    { wch: 24 },
    { wch: 56 },
    { wch: 18 },
  ];

  XLSX.utils.book_append_sheet(wb, ticketsSheet, 'All Tickets');
  XLSX.utils.book_append_sheet(wb, timelineSheet, 'Timeline Details');

  const dateLabel = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(wb, `CS_Kenya_Tickets_${dateLabel}.xlsx`);
};

const CSK_USER_MANUAL_PDF_URL = `${process.env.PUBLIC_URL || ''}/docs/Customer-Services-Kenya-User-Manual.pdf`;

const downloadCskUserManual = () => {
  const link = document.createElement('a');
  link.href = CSK_USER_MANUAL_PDF_URL;
  link.download = 'Customer-Services-Kenya-User-Manual.pdf';
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const renderStars = (rating, size = 'h-4 w-4') =>
  [1, 2, 3, 4, 5].map((star) => (
    <Star
      key={star}
      className={`${size} ${star <= rating ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`}
      aria-hidden
    />
  ));

const SEED_TICKETS = [
  {
    id: 'WO-2410',
    ticketType: 'Installation',
    typeCode: 'WO',
    client: CLIENT_ACCOUNT.name,
    contact: CLIENT_ACCOUNT.contact,
    location: CLIENT_ACCOUNT.address,
    coordinates: { latitude: '-1.2650', longitude: '36.8100' },
    coordinatesLabel: '-1.2650, 36.8100',
    issue: 'New FTTH drop required — 4-unit building, ground floor access',
    category: 'Installation',
    priority: '7 Days',
    status: 'Open',
    created: '2026-08-17',
    engineer: '',
    workOrderId: '',
    updates: [
      { at: '2026-08-17T08:00:00', message: 'Installation (WO) opened by client', by: 'Client' },
      { at: '2026-08-17T08:00:00', message: 'Site coordinates: -1.2650, 36.8100', by: 'Client' },
      { at: '2026-08-17T08:01:00', message: 'Ticket received — awaiting admin review', by: 'System' },
    ],
  },
  {
    id: 'TT-2405',
    ticketType: 'Maintenance',
    typeCode: 'TT',
    client: CLIENT_ACCOUNT.name,
    contact: CLIENT_ACCOUNT.contact,
    location: CLIENT_ACCOUNT.address,
    coordinates: { latitude: '-1.2674', longitude: '36.8070' },
    coordinatesLabel: '-1.2674, 36.8070',
    issue: 'Wi-Fi drops every evening — router reboot needed',
    category: 'Intermittent connection',
    priority: 'Minor',
    status: 'In Progress',
    created: '2026-08-15',
    engineer: 'Cromwell Oronje',
    workOrderId: 'WO-2405',
    updates: [
      { at: '2026-08-15T07:00:00', message: 'Maintenance (TT) opened by client', by: 'Client' },
      { at: '2026-08-15T07:00:00', message: 'Site coordinates: -1.2674, 36.8070', by: 'Client' },
      { at: '2026-08-15T07:01:00', message: 'Ticket received — awaiting admin review', by: 'System' },
      { at: '2026-08-15T08:30:00', message: 'Team leader Cromwell Oronje assigned', by: 'Admin' },
      { at: '2026-08-15T08:31:00', message: 'Trouble ticket TT-2405 issued as work order WO-2405', by: 'Admin' },
    ],
  },
  {
    id: 'TT-2401',
    ticketType: 'Maintenance',
    typeCode: 'TT',
    client: CLIENT_ACCOUNT.name,
    contact: CLIENT_ACCOUNT.contact,
    location: CLIENT_ACCOUNT.address,
    coordinates: { latitude: '-1.2674', longitude: '36.8070' },
    coordinatesLabel: '-1.2674, 36.8070',
    issue: 'No connectivity — ONT offline',
    category: 'No connectivity',
    priority: 'Critical',
    status: 'In Progress',
    created: '2026-08-14',
    engineer: 'Cromwell Oronje',
    workOrderId: 'WO-2401',
    updates: [
      { at: '2026-08-14T08:15:00', message: 'Maintenance (TT) opened by client', by: 'Client' },
      { at: '2026-08-14T08:15:00', message: 'Site coordinates: -1.2674, 36.8070', by: 'Client' },
      { at: '2026-08-14T09:00:00', message: 'Ticket acknowledged by support', by: 'Admin' },
      { at: '2026-08-14T10:30:00', message: 'Engineer Cromwell Oronje assigned', by: 'Admin' },
      { at: '2026-08-14T11:15:00', message: 'Engineer confirmed tools & materials: OTDR / fiber tester, Optical power meter, Patch cord 3m SC/APC × 2', by: 'Cromwell Oronje' },
      { at: '2026-08-14T11:30:00', message: 'TT-2401 issued as work order WO-2401', by: 'Admin' },
      { at: '2026-08-14T12:00:00', message: 'Field work started on site', by: 'Cromwell Oronje' },
    ],
  },
  {
    id: 'TT-2399',
    ticketType: 'Maintenance',
    typeCode: 'TT',
    client: CLIENT_ACCOUNT.name,
    contact: CLIENT_ACCOUNT.contact,
    location: CLIENT_ACCOUNT.address,
    coordinates: { latitude: '-1.2674', longitude: '36.8070' },
    coordinatesLabel: '-1.2674, 36.8070',
    issue: 'Slow speed during peak hours — speed test below 20 Mbps',
    category: 'Slow speed',
    priority: 'Major',
    status: 'Resolved',
    created: '2026-08-12',
    engineer: 'Cromwell Oronje',
    workOrderId: 'WO-2399',
    clientConfirmed: false,
    updates: [
      { at: '2026-08-12T10:00:00', message: 'Maintenance (TT) opened by client', by: 'Client' },
      { at: '2026-08-12T10:00:00', message: 'Site coordinates: -1.2674, 36.8070', by: 'Client' },
      { at: '2026-08-12T14:00:00', message: 'Engineer Cromwell Oronje assigned', by: 'Admin' },
      { at: '2026-08-13T09:00:00', message: 'Engineer confirmed tools & materials: OTDR / fiber tester, CPE installation toolkit', by: 'Cromwell Oronje' },
      { at: '2026-08-13T11:30:00', message: 'Work completed — materials reconciled (OTDR / fiber tester: 1/1; CPE installation toolkit: 1/1)', by: 'Cromwell Oronje' },
    ],
  },
  {
    id: 'TT-2402',
    ticketType: 'Maintenance',
    typeCode: 'TT',
    client: 'Mombasa Bay Apartments',
    contact: '+254 733 000 202',
    location: 'Nyali, Mombasa',
    coordinates: { latitude: '-4.0435', longitude: '39.7167' },
    coordinatesLabel: '-4.0435, 39.7167',
    issue: 'Intermittent speed drops below 10 Mbps',
    category: 'Slow speed',
    priority: 'Major',
    status: 'In Progress',
    created: '2026-08-13',
    engineer: 'Maxwel Ougo',
    workOrderId: 'WO-2402',
    updates: [
      { at: '2026-08-13T14:00:00', message: 'Maintenance (TT) opened by client', by: 'Client' },
      { at: '2026-08-13T14:00:00', message: 'Site coordinates: -4.0435, 39.7167', by: 'Client' },
      { at: '2026-08-13T15:20:00', message: 'Engineer assigned', by: 'Admin' },
      { at: '2026-08-13T15:21:00', message: 'TT-2402 issued as work order WO-2402', by: 'Admin' },
      { at: '2026-08-14T08:00:00', message: 'Engineer confirmed tools & materials: Optical power meter, Drop cable 2c × 3', by: 'Maxwel Ougo' },
      { at: '2026-08-14T09:30:00', message: 'Field work started on site', by: 'Maxwel Ougo' },
    ],
  },
  {
    id: 'TT-2403',
    ticketType: 'Maintenance',
    typeCode: 'TT',
    client: 'Kisumu Smart Office',
    contact: '+254 722 000 303',
    location: 'Milimani, Kisumu',
    coordinates: { latitude: '-0.1022', longitude: '34.7617' },
    coordinatesLabel: '-0.1022, 34.7617',
    issue: 'Billing query — duplicate charge',
    category: 'Billing / account',
    priority: 'Minor',
    status: 'Resolved',
    created: '2026-08-10',
    engineer: 'Pancras Okelo',
    workOrderId: 'WO-2403',
    clientConfirmed: true,
    serviceRating: 5,
    confirmedAt: '2026-08-12T10:00:00',
    updates: [
      { at: '2026-08-10T09:00:00', message: 'Maintenance (TT) opened by client', by: 'Client' },
      { at: '2026-08-10T09:00:00', message: 'Site coordinates: -0.1022, 34.7617', by: 'Client' },
      { at: '2026-08-11T16:00:00', message: 'Work completed — materials reconciled (CPE installation toolkit: 1/1)', by: 'Pancras Okelo' },
      { at: '2026-08-12T10:00:00', message: 'Client confirmed resolved — service rated 5/5', by: 'Client' },
    ],
  },
  {
    id: 'TT-2404',
    ticketType: 'Maintenance',
    typeCode: 'TT',
    client: 'Nakuru Business Park',
    contact: '+254 700 000 404',
    location: 'Section 58, Nakuru',
    coordinates: { latitude: '-0.3031', longitude: '36.0800' },
    coordinatesLabel: '-0.3031, 36.0800',
    issue: 'Fiber cut reported near junction',
    category: 'Equipment fault',
    priority: 'Critical',
    status: 'In Progress',
    created: '2026-08-14',
    engineer: 'Geoffrey Mwangi Nduati',
    workOrderId: 'WO-2404',
    updates: [
      { at: '2026-08-14T06:00:00', message: 'Maintenance (TT) opened by client', by: 'Client' },
      { at: '2026-08-14T06:00:00', message: 'Site coordinates: -0.3031, 36.0800', by: 'Client' },
      { at: '2026-08-14T07:00:00', message: 'Engineer Geoffrey Mwangi Nduati dispatched', by: 'Admin' },
      { at: '2026-08-14T07:01:00', message: 'TT-2404 issued as work order WO-2404', by: 'Admin' },
    ],
  },
  {
    id: 'TT-2388',
    ticketType: 'Maintenance',
    typeCode: 'TT',
    client: 'Nakuru Business Park',
    contact: '+254 700 000 404',
    location: 'Section 58, Nakuru',
    coordinates: { latitude: '-0.3031', longitude: '36.0800' },
    coordinatesLabel: '-0.3031, 36.0800',
    issue: 'Core router failover — emergency restoration',
    category: 'Equipment fault',
    priority: 'Critical',
    status: 'Resolved',
    created: '2026-08-11',
    engineer: 'Geoffrey Mwangi Nduati',
    workOrderId: 'WO-2388',
    clientConfirmed: true,
    serviceRating: 4,
    confirmedAt: '2026-08-14T09:30:00',
    updates: [
      { at: '2026-08-11T07:00:00', message: 'Maintenance (TT) opened by client', by: 'Client' },
      { at: '2026-08-11T07:00:00', message: 'Site coordinates: -0.3031, 36.0800', by: 'Client' },
      { at: '2026-08-11T07:30:00', message: 'Work order WO-2388 issued to Geoffrey Mwangi Nduati', by: 'Admin' },
      { at: '2026-08-11T08:00:00', message: 'Engineer confirmed tools & materials: Fusion splicer, FO Aerial Cable 24c × 2, Splice enclosure 24c × 1', by: 'Geoffrey Mwangi Nduati' },
      { at: '2026-08-13T16:00:00', message: 'Work completed — materials reconciled (Fusion splicer: 1/1; FO Aerial Cable 24c: 2/2; Splice enclosure 24c: 1/1)', by: 'Geoffrey Mwangi Nduati' },
      { at: '2026-08-13T16:01:00', message: '1 site photo(s) uploaded', by: 'Geoffrey Mwangi Nduati' },
      { at: '2026-08-14T09:30:00', message: 'Client confirmed resolved — service rated 4/5', by: 'Client' },
    ],
  },
];

const SEED_WORK_ORDERS = [
  {
    id: 'WO-2405',
    ticketId: 'TT-2405',
    title: 'Router reboot & Wi-Fi stability check — Westlands',
    engineer: 'Cromwell Oronje',
    status: 'Assigned',
    scheduled: '2026-08-17 10:00',
    zone: 'Nairobi',
    issuedAt: '2026-08-15T08:31:00',
  },
  {
    id: 'WO-2401',
    ticketId: 'TT-2401',
    title: 'ONT diagnostics & fiber check — Westlands',
    engineer: 'Cromwell Oronje',
    status: 'In Progress',
    scheduled: '2026-08-14 14:00',
    zone: 'Nairobi',
    issuedAt: '2026-08-14T11:30:00',
    workConfirmed: true,
    workConfirmedAt: '2026-08-14T11:15:00',
    materialPlan: [
      { id: 'otdr', label: 'OTDR / fiber tester', kind: 'tool', plannedQty: 1 },
      { id: 'power-meter', label: 'Optical power meter', kind: 'tool', plannedQty: 1 },
      { id: 'patch-cord-sc-apc', label: 'Patch cord 3m SC/APC', kind: 'material', plannedQty: 2 },
    ],
    selectedTools: ['OTDR / fiber tester', 'Optical power meter', 'Patch cord 3m SC/APC × 2'],
  },
  {
    id: 'WO-2402',
    ticketId: 'TT-2402',
    title: 'Speed test & CPE reboot — Nyali',
    engineer: 'Maxwel Ougo',
    status: 'In Progress',
    scheduled: '2026-08-15 09:00',
    zone: 'Coast',
    issuedAt: '2026-08-13T15:21:00',
    workConfirmed: true,
    workConfirmedAt: '2026-08-14T08:00:00',
    materialPlan: [
      { id: 'power-meter', label: 'Optical power meter', kind: 'tool', plannedQty: 1 },
      { id: 'drop-cable-2c', label: 'Drop cable 2c', kind: 'material', plannedQty: 3 },
    ],
    selectedTools: ['Optical power meter', 'Drop cable 2c × 3'],
  },
  {
    id: 'WO-2404',
    ticketId: 'TT-2404',
    title: 'Emergency splice — Nakuru Section 58',
    engineer: 'Geoffrey Mwangi Nduati',
    status: 'Assigned',
    scheduled: '2026-08-17 07:30',
    zone: 'Rift Valley',
    issuedAt: '2026-08-14T07:01:00',
  },
  {
    id: 'WO-2403',
    ticketId: 'TT-2403',
    title: 'Billing adjustment follow-up',
    engineer: 'Pancras Okelo',
    status: 'Closed',
    scheduled: '2026-08-11 14:00',
    zone: 'Western',
    issuedAt: '2026-08-10T09:00:00',
    completedAt: '2026-08-11T16:00:00',
    workConfirmed: true,
    workConfirmedAt: '2026-08-10T10:00:00',
    materialsReconciled: true,
    materialPlan: [
      { id: 'cpe-toolkit', label: 'CPE installation toolkit', kind: 'tool', plannedQty: 1 },
    ],
    reconciledMaterials: [
      { id: 'cpe-toolkit', label: 'CPE installation toolkit', kind: 'tool', plannedQty: 1, usedQty: 1 },
    ],
    selectedTools: ['CPE installation toolkit'],
  },
  {
    id: 'WO-2399',
    ticketId: 'TT-2399',
    title: 'Peak-hour speed optimization — Westlands',
    engineer: 'Cromwell Oronje',
    status: 'Closed',
    scheduled: '2026-08-13 10:00',
    zone: 'Nairobi',
    issuedAt: '2026-08-12T10:00:00',
    completedAt: '2026-08-13T11:30:00',
    workConfirmed: true,
    workConfirmedAt: '2026-08-13T09:00:00',
    materialsReconciled: true,
    materialPlan: [
      { id: 'otdr', label: 'OTDR / fiber tester', kind: 'tool', plannedQty: 1 },
      { id: 'cpe-toolkit', label: 'CPE installation toolkit', kind: 'tool', plannedQty: 1 },
    ],
    reconciledMaterials: [
      { id: 'otdr', label: 'OTDR / fiber tester', kind: 'tool', plannedQty: 1, usedQty: 1 },
      { id: 'cpe-toolkit', label: 'CPE installation toolkit', kind: 'tool', plannedQty: 1, usedQty: 1 },
    ],
    selectedTools: ['OTDR / fiber tester', 'CPE installation toolkit'],
  },
  {
    id: 'WO-2388',
    ticketId: 'TT-2388',
    title: 'Emergency core router restoration — Nakuru',
    engineer: 'Geoffrey Mwangi Nduati',
    status: 'Closed',
    scheduled: '2026-08-11 08:00',
    zone: 'Rift Valley',
    issuedAt: '2026-08-11T07:30:00',
    completedAt: '2026-08-13T16:00:00',
    delayReason: 'Spare core module procurement delayed — vendor lead time 36 hours',
    workConfirmed: true,
    workConfirmedAt: '2026-08-11T08:00:00',
    materialsReconciled: true,
    materialPlan: [
      { id: 'fusion-splicer', label: 'Fusion splicer', kind: 'tool', plannedQty: 1 },
      { id: 'fo-aerial-24c', label: 'FO Aerial Cable 24c', kind: 'material', plannedQty: 2 },
      { id: 'splice-enclosure-24c', label: 'Splice enclosure 24c', kind: 'material', plannedQty: 1 },
    ],
    reconciledMaterials: [
      { id: 'fusion-splicer', label: 'Fusion splicer', kind: 'tool', plannedQty: 1, usedQty: 1 },
      { id: 'fo-aerial-24c', label: 'FO Aerial Cable 24c', kind: 'material', plannedQty: 2, usedQty: 2 },
      { id: 'splice-enclosure-24c', label: 'Splice enclosure 24c', kind: 'material', plannedQty: 1, usedQty: 1 },
    ],
    selectedTools: ['Fusion splicer', 'FO Aerial Cable 24c × 2', 'Splice enclosure 24c'],
    sitePhotos: [{ name: 'nakuru-splice-complete.jpg', dataUrl: '', uploadedAt: '2026-08-13T16:00:00' }],
  },
];

const SEED_NOTIFICATIONS = [
  {
    id: 'N-001',
    client: CLIENT_ACCOUNT.name,
    ticketId: 'TT-2401',
    title: 'Work in progress',
    message: 'Cromwell Oronje has started work on ticket TT-2401.',
    createdAt: '2026-08-14T12:00:00',
    read: false,
  },
  {
    id: 'N-002',
    client: CLIENT_ACCOUNT.name,
    ticketId: 'TT-2399',
    title: 'Issue resolved',
    message: 'Your ticket TT-2399 has been resolved. Please confirm and rate our service.',
    createdAt: '2026-08-13T11:30:00',
    read: false,
  },
  {
    id: 'N-003',
    client: CLIENT_ACCOUNT.name,
    ticketId: 'WO-2410',
    title: 'Ticket opened',
    message: 'Your WO ticket WO-2410 has been logged. We will update you shortly.',
    createdAt: '2026-08-17T08:01:00',
    read: false,
  },
  {
    id: 'EN-001',
    engineer: 'Cromwell Oronje',
    workOrderId: 'WO-2405',
    ticketId: 'TT-2405',
    title: 'New work order assigned',
    message: 'WO-2405 — Router reboot & Wi-Fi stability check. Select tools & materials before starting.',
    createdAt: '2026-08-15T08:31:00',
    read: false,
  },
  {
    id: 'EN-002',
    engineer: 'Cromwell Oronje',
    workOrderId: 'WO-2401',
    ticketId: 'TT-2401',
    title: 'Reconcile materials',
    message: 'WO-2401 is in progress — reconcile materials and complete the job when finished on site.',
    createdAt: '2026-08-16T09:00:00',
    read: false,
  },
  {
    id: 'A-001',
    admin: true,
    ticketId: 'WO-2410',
    title: 'New installation ticket',
    message: 'Client opened installation WO-2410 — timeline 7 Days. Review and assign engineer.',
    createdAt: '2026-08-17T08:01:00',
    read: false,
  },
];

function WorkOrderMaterialsSummary({ workOrder, variant = 'default' }) {
  if (!workOrder) return null;
  const plan = getWorkOrderMaterialPlan(workOrder);
  const reconciled = workOrder.reconciledMaterials;
  if (!plan.length && !reconciled?.length) return null;

  const boxCls =
    variant === 'admin'
      ? 'mt-4 rounded-xl border border-violet-200 bg-white p-4'
      : 'mt-4 rounded-xl border border-slate-200 bg-white p-4';

  return (
    <section className={boxCls}>
      <h3 className="text-sm font-semibold text-slate-900">Tools & materials</h3>
      {plan.length ? (
        <div className="mt-2">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Planned</p>
          <ul className="mt-1 space-y-1 text-sm text-slate-700">
            {plan.map((item) => (
              <li key={`plan-${item.id}`} className="flex justify-between gap-2">
                <span>{item.label}</span>
                <span className="font-medium text-slate-900">× {item.plannedQty || 1}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {reconciled?.length ? (
        <div className="mt-3 border-t border-slate-100 pt-3">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Reconciled (used)</p>
          <ul className="mt-1 space-y-1 text-sm text-slate-700">
            {reconciled.map((item) => (
              <li key={`rec-${item.id}`} className="flex justify-between gap-2">
                <span>{item.label}</span>
                <span className="font-medium text-slate-900">
                  {item.usedQty} / {item.plannedQty}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {workOrder.sitePhotos?.length ? (
        <p className="mt-2 text-xs text-slate-500">{workOrder.sitePhotos.length} site photo(s) attached</p>
      ) : null}
    </section>
  );
}

function playAdminBeep() {
  try {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return;

    const ctx = new AudioContextClass();
    const playTone = (frequency, start, duration) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = frequency;
      osc.connect(gain);
      gain.connect(ctx.destination);
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.12, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
      osc.start(start);
      osc.stop(start + duration);
    };

    const now = ctx.currentTime;
    playTone(880, now, 0.18);
    playTone(1100, now + 0.22, 0.18);
    window.setTimeout(() => {
      ctx.close().catch(() => {});
    }, 600);
  } catch {
    // Audio may be blocked until user interaction; visual alert still applies.
  }
}

function TimelineBadge({ timeline }) {
  const value = timeline;
  const cls = isPriorityValue(value)
    ? PRIORITY_STYLES[value] || 'bg-slate-100 text-slate-700'
    : TIMELINE_STYLES[normalizeTimelineValue(value)] || 'bg-slate-100 text-slate-700';
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>
      {formatTicketPriorityLabel(value)}
    </span>
  );
}

function Badge({ label, map }) {
  const cls = map[label] || 'bg-slate-100 text-slate-700';
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>
      {label}
    </span>
  );
}

function StatCard({ label, value, icon: Icon, accent, isActive, onClick, hasAlert, alertCount, alertHint, compact = false }) {
  const className = compact
    ? `csk-admin-metric-card relative ${hasAlert ? 'has-alert' : ''} ${isActive ? 'is-active' : ''}`
    : `relative rounded-xl border bg-white p-4 text-left shadow-sm transition-all ${
        hasAlert
          ? 'animate-pulse border-rose-400 ring-2 ring-rose-300 ring-offset-1'
          : isActive
            ? 'border-sky-400 ring-2 ring-sky-200'
            : 'border-slate-200 hover:border-slate-300 hover:shadow-md'
      }`;

  const content = compact ? (
    <>
      {hasAlert ? (
        <span className="csk-admin-metric-alert">
          {alertCount > 1 ? `${alertCount} new` : 'New'}
        </span>
      ) : null}
      <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${accent}`} aria-hidden>
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm text-slate-500">{label}</p>
        <p className="mt-0.5 text-2xl font-bold tracking-tight text-slate-900">{value}</p>
        {hasAlert && alertHint ? (
          <p className="mt-0.5 truncate text-xs font-medium text-rose-600">{alertHint}</p>
        ) : null}
      </div>
    </>
  ) : (
    <>
      {hasAlert ? (
        <span className="absolute -right-2 -top-2 inline-flex min-w-[1.5rem] items-center justify-center rounded-full bg-rose-600 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white shadow-sm">
          {alertCount > 1 ? `${alertCount} new` : 'New'}
        </span>
      ) : null}
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm text-slate-500">{label}</p>
          <p className="mt-1 text-2xl font-semibold text-slate-900">{value}</p>
          {hasAlert && alertHint ? (
            <p className="mt-1 text-xs font-medium text-rose-600">{alertHint}</p>
          ) : null}
        </div>
        <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${accent}`} aria-hidden>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`${className} w-full cursor-pointer text-left`}
        aria-pressed={isActive}
        aria-live={hasAlert ? 'polite' : undefined}
      >
        {content}
      </button>
    );
  }

  return <div className={className}>{content}</div>;
}

function AdminDashboardBarChart({ title, subtitle, items }) {
  const maxValue = Math.max(...items.map((item) => item.value), 1);

  return (
    <section className="csk-admin-widget csk-admin-widget--chart">
      <div className="csk-admin-widget-head">
        <div>
          <h2 className="text-base font-bold text-slate-900">{title}</h2>
          {subtitle ? <p className="mt-0.5 text-sm text-slate-500">{subtitle}</p> : null}
        </div>
      </div>
      <div className="csk-admin-chart-body">
        <div className="csk-admin-bar-chart" role="img" aria-label={title}>
          {items.map((item) => (
            <div key={item.label} className="csk-admin-bar-chart-col">
              <div className="csk-admin-bar-chart-track">
                <div
                  className={`csk-admin-bar-chart-fill ${item.highlight ? 'is-highlight' : ''}`}
                  style={{ height: `${Math.max(8, (item.value / maxValue) * 100)}%` }}
                  title={`${item.label}: ${item.value}`}
                />
              </div>
              <span className="csk-admin-bar-chart-value">{item.value}</span>
              <span className="csk-admin-bar-chart-label">{item.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const teamRankStyles = {
  'Team Leader': 'bg-violet-100 text-violet-800 ring-violet-200',
  Engineer: 'bg-teal-100 text-teal-800 ring-teal-200',
  Technician: 'bg-slate-100 text-slate-700 ring-slate-200',
};

const teamAvatarStyles = {
  'Team Leader': 'csk-team-avatar--leader',
  Engineer: 'csk-team-avatar--engineer',
  Technician: 'csk-team-avatar--technician',
};

function GenderAvatar({ gender = 'male', rank, size = 'md', className = '' }) {
  const isFemale = gender === 'female';
  const sizeClass = size === 'sm' ? 'csk-gender-avatar--sm' : 'csk-gender-avatar--md';
  const rankClass = rank ? teamAvatarStyles[rank] : '';

  return (
    <div
      className={`csk-gender-avatar ${isFemale ? 'is-female' : 'is-male'} ${sizeClass} ${rankClass} ${className}`.trim()}
      aria-hidden
    >
      {isFemale ? (
        <svg viewBox="0 0 24 24" fill="currentColor" className="csk-gender-avatar-icon">
          <path d="M12 3.5a4.2 4.2 0 0 1 4.1 3.3 4.6 4.6 0 0 1-1.2 4.1 5.8 5.8 0 0 1 3.4 5.3V21h-2.2v-4.1a3.6 3.6 0 0 0-7.2 0V21H6.7v-4.8a5.8 5.8 0 0 1 3.4-5.3 4.6 4.6 0 0 1-1.2-4.1A4.2 4.2 0 0 1 12 3.5Z" />
          <path d="M8.2 5.8c.6-1.6 2-2.8 3.8-2.8s3.2 1.2 3.8 2.8" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      ) : (
        <svg viewBox="0 0 24 24" fill="currentColor" className="csk-gender-avatar-icon">
          <circle cx="12" cy="8.5" r="3.8" />
          <path d="M5.5 20.5c0-3.6 2.9-6.5 6.5-6.5s6.5 2.9 6.5 6.5" />
        </svg>
      )}
    </div>
  );
}

function AssignedEngineerContactCard({ engineerName, workOrderStatus, variant = 'client' }) {
  if (!engineerName) return null;

  const member = getTeamMemberByName(engineerName);
  const accent = variant === 'admin' ? 'from-violet-50 to-white border-violet-200' : 'from-teal-50 to-white border-teal-200';
  const titleAccent = variant === 'admin' ? 'text-violet-800' : 'text-teal-800';

  return (
    <section className={`rounded-xl border bg-gradient-to-br p-4 ${accent}`}>
      <h3 className={`text-sm font-semibold ${titleAccent}`}>Assigned engineer</h3>
      <p className="mt-0.5 text-xs text-slate-500">Contact details for this in-progress work order</p>
      <div className="mt-4 flex gap-3">
        <GenderAvatar
          gender={member?.gender || getTeamMemberGender(engineerName)}
          rank={member?.rank}
          size="md"
        />
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-slate-900">{engineerName}</p>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            {member?.rank ? (
              <span
                className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ring-1 ring-inset ${
                  teamRankStyles[member.rank] || teamRankStyles.Technician
                }`}
              >
                {member.rank}
              </span>
            ) : null}
            {workOrderStatus ? <Badge label={workOrderStatus} map={STATUS_STYLES} /> : null}
          </div>
          {member?.specialty ? (
            <p className="mt-2 text-sm text-slate-600">{member.specialty}</p>
          ) : null}
          {member?.zone ? <p className="mt-0.5 text-xs text-slate-500">{member.zone} zone</p> : null}
        </div>
      </div>
      <dl className="mt-4 space-y-2.5 border-t border-slate-200/80 pt-4 text-sm">
        <div className="flex items-center gap-2.5 text-slate-700">
          <Phone className="h-4 w-4 shrink-0 text-slate-400" aria-hidden />
          <div className="min-w-0">
            <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Phone</dt>
            <dd className="mt-0.5 font-medium text-slate-900">
              {member?.phone ? (
                <a href={`tel:${member.phone.replace(/\s/g, '')}`} className="hover:text-teal-700">
                  {member.phone}
                </a>
              ) : (
                '—'
              )}
            </dd>
          </div>
        </div>
        <div className="flex items-center gap-2.5 text-slate-700">
          <Mail className="h-4 w-4 shrink-0 text-slate-400" aria-hidden />
          <div className="min-w-0">
            <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Email</dt>
            <dd className="mt-0.5 truncate font-medium text-slate-900">
              {member?.email ? (
                <a href={`mailto:${member.email}`} className="hover:text-teal-700">
                  {member.email}
                </a>
              ) : (
                '—'
              )}
            </dd>
          </div>
        </div>
      </dl>
    </section>
  );
}

function AdminTeamMemberCard({ member, availability, activeJobs }) {
  const isBusy = availability === 'Busy';

  return (
    <article className="csk-admin-team-card">
      <GenderAvatar gender={member.gender} rank={member.rank} size="md" />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="font-semibold text-slate-900">{member.name}</h3>
          <span
            className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ring-1 ring-inset ${
              teamRankStyles[member.rank] || teamRankStyles.Technician
            }`}
          >
            {member.rank}
          </span>
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
              isBusy ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'
            }`}
          >
            <span className={`h-1.5 w-1.5 rounded-full ${isBusy ? 'bg-amber-500' : 'bg-emerald-500'}`} />
            {availability}
          </span>
        </div>
        <p className="mt-1 text-sm text-slate-600">{member.specialty}</p>
        <p className="mt-0.5 text-xs text-slate-500">{member.zone} zone</p>
        {isBusy ? (
          <p className="mt-1 text-xs font-medium text-amber-700">
            {activeJobs} active work order{activeJobs === 1 ? '' : 's'}
          </p>
        ) : null}
        <dl className="mt-3 grid gap-2 sm:grid-cols-2">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Phone className="h-3.5 w-3.5 shrink-0 text-violet-500" aria-hidden />
            <a href={`tel:${member.phone.replace(/\s/g, '')}`} className="truncate hover:text-violet-700">
              {member.phone}
            </a>
          </div>
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Mail className="h-3.5 w-3.5 shrink-0 text-violet-500" aria-hidden />
            <a href={`mailto:${member.email}`} className="truncate hover:text-violet-700">
              {member.email}
            </a>
          </div>
        </dl>
      </div>
    </article>
  );
}

function AdminTechnicalTeamPanel({ team, workOrders }) {
  const grouped = useMemo(() => {
    const sorted = [...team].sort(
      (a, b) => (TEAM_RANK_ORDER[a.rank] ?? 99) - (TEAM_RANK_ORDER[b.rank] ?? 99)
    );
    return ['Team Leader', 'Engineer', 'Technician']
      .map((rank) => ({
        rank,
        members: sorted.filter((member) => member.rank === rank),
      }))
      .filter((group) => group.members.length > 0);
  }, [team]);

  const availableCount = team.filter(
    (member) => getTeamMemberAvailability(member.name, workOrders) === 'Available'
  ).length;
  const busyCount = team.length - availableCount;

  return (
    <section className="csk-admin-widget csk-admin-widget--team">
      <div className="csk-admin-widget-head">
        <div>
          <h2 className="text-base font-bold text-slate-900">Technical team</h2>
          <p className="mt-0.5 text-sm text-slate-500">
            Team leaders, engineers, and technicians — availability based on active work orders
          </p>
        </div>
        <span className="csk-admin-widget-link">
          {availableCount} available · {busyCount} busy
        </span>
      </div>
      <div className="csk-admin-team-body">
        {grouped.map(({ rank, members }) => (
          <div key={rank} className="csk-admin-team-group">
            <h3 className="csk-admin-team-group-title">{rank}s</h3>
            <div className="csk-admin-team-grid">
              {members.map((member) => (
                <AdminTeamMemberCard
                  key={member.id}
                  member={member}
                  availability={getTeamMemberAvailability(member.name, workOrders)}
                  activeJobs={getTeamMemberActiveJobs(member.name, workOrders)}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function AdminReportMetric({ label, value }) {
  return (
    <div className="csk-admin-report-metric">
      <p className="text-sm text-slate-500">{label}</p>
      <p className="mt-1 text-2xl font-bold tracking-tight text-slate-900">{value}</p>
    </div>
  );
}

function AdminOperationsReportPanel({
  tickets,
  workOrders,
  openCount,
  inProgressCount,
  resolvedCount,
  awaitingClientCount,
  activeWo,
  slaSplit,
  teamAvailableCount,
  onExportExcel,
}) {
  const woAssigned = workOrders.filter((w) => w.status === 'Assigned').length;
  const woInProgress = workOrders.filter((w) => w.status === 'In Progress').length;
  const woClosed = workOrders.filter((w) => w.status === 'Closed').length;
  const criticalCount = tickets.filter((t) => t.priority === 'Critical').length;
  const majorCount = tickets.filter((t) => t.priority === 'Major').length;
  const minorCount = tickets.filter((t) => t.priority === 'Minor').length;
  const countTimeline = (value) =>
    tickets.filter((t) => normalizeTimelineValue(t.priority) === value).length;
  const hr24Count = countTimeline('24 HR');
  const days7Count = countTimeline('7 Days');
  const weeks2Count = countTimeline('2 Weeks');

  return (
    <div className="csk-admin-report space-y-4">
      <section className="csk-admin-widget">
        <div className="csk-admin-widget-head">
          <div>
            <h2 className="text-base font-bold text-slate-900">Operations report</h2>
            <p className="mt-0.5 text-sm text-slate-500">
              Snapshot of tickets, work orders, SLA performance, and team capacity
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={onExportExcel}
              className="csk-admin-action-btn inline-flex items-center gap-2"
            >
              <Download className="h-4 w-4" aria-hidden />
              Export Excel
            </button>
            <span className="csk-admin-widget-link">{tickets.length} tickets</span>
          </div>
        </div>
        <div className="csk-admin-report-body grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <AdminReportMetric label="Open tickets" value={openCount} />
          <AdminReportMetric label="In progress" value={inProgressCount} />
          <AdminReportMetric label="Resolved (confirmed)" value={resolvedCount} />
          <AdminReportMetric label="Awaiting client" value={awaitingClientCount} />
          <AdminReportMetric label="Active work orders" value={activeWo} />
          <AdminReportMetric label="On-time closures" value={slaSplit.onTime.length} />
          <AdminReportMetric label="Delayed closures" value={slaSplit.delayed.length} />
          <AdminReportMetric label="Team available" value={teamAvailableCount} />
        </div>
      </section>

      <div className="grid gap-4 lg:grid-cols-2">
        <AdminDashboardBarChart
          title="Ticket statistics"
          subtitle="Current distribution across queues"
          items={[
            { label: 'Open', value: openCount, highlight: true },
            { label: 'In progress', value: inProgressCount },
            { label: 'Awaiting', value: awaitingClientCount },
            { label: 'Resolved', value: resolvedCount },
            { label: 'Total', value: tickets.length },
          ]}
        />
        <AdminDashboardBarChart
          title="Work order status"
          subtitle="Field assignments breakdown"
          items={[
            { label: 'Assigned', value: woAssigned, highlight: true },
            { label: 'In progress', value: woInProgress },
            { label: 'Closed', value: woClosed },
            { label: 'Active', value: activeWo },
            { label: 'Total', value: workOrders.length },
          ]}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <AdminDashboardBarChart
          title="SLA completion"
          subtitle="Client-confirmed closure outcomes"
          items={[
            { label: 'On time', value: slaSplit.onTime.length, highlight: true },
            { label: 'Delayed', value: slaSplit.delayed.length },
            { label: 'Critical', value: criticalCount },
            { label: 'Major', value: majorCount },
            { label: 'Minor', value: minorCount },
            { label: '24 HR', value: hr24Count },
            { label: '7 Days', value: days7Count },
            { label: '2 Weeks', value: weeks2Count },
          ]}
        />
        <AdminDashboardBarChart
          title="Priority & timeline mix"
          subtitle="Maintenance priority (TT) and installation timelines (WO)"
          items={[
            { label: 'Critical', value: criticalCount, highlight: true },
            { label: 'Major', value: majorCount },
            { label: 'Minor', value: minorCount },
            { label: '24 HR', value: hr24Count },
            { label: '7 Days', value: days7Count },
            { label: '2 Weeks', value: weeks2Count },
            { label: 'Open', value: openCount },
            { label: 'Resolved', value: resolvedCount },
          ]}
        />
      </div>
    </div>
  );
}

function RoleViewBanner({ role }) {
  const config = ROLE_CONFIG[role];
  const Icon = config.icon;

  return (
    <section className={`rounded-xl border px-4 py-4 sm:px-5 ${config.accent}`} aria-label={`${role} view`}>
      <div className="flex items-start gap-3">
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${config.iconWrap}`}>
          <Icon className="h-5 w-5" aria-hidden />
        </div>
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wide opacity-80">{role} view</p>
          <h2 className="mt-0.5 text-base font-semibold sm:text-lg">{config.title}</h2>
          <p className="mt-1 text-sm opacity-90">{config.description}</p>
        </div>
      </div>
    </section>
  );
}

function HorizontalStepperConnector({ filled, fillClass, animateDrop }) {
  return (
    <div className="relative flex h-3 min-w-[0.75rem] flex-1 items-center px-0.5" aria-hidden>
      <div className="csk-stepper-rail-h absolute inset-x-0.5" />
      {filled ? (
        <div
          key={animateDrop ? `drop-h-${animateDrop}` : 'filled-h'}
          className={`absolute left-0.5 right-auto ${
            animateDrop ? `csk-stepper-segment-drop-h ${fillClass}` : `csk-stepper-segment-filled-h ${fillClass}`
          }`}
        />
      ) : null}
    </div>
  );
}

function TicketProgressStepper({ ticket }) {
  const activeIndex = getProgressIndex(ticket);
  const fullyConfirmed = ticket.status === 'Resolved' && ticket.clientConfirmed;
  const pendingConfirmation = ticket.status === 'Resolved' && !ticket.clientConfirmed;

  const getStepMeta = (index) => {
    const isResolvedStep = index === PROGRESS_STEPS.length - 1;
    const done =
      index < activeIndex ||
      (isResolvedStep && fullyConfirmed) ||
      (index <= activeIndex && fullyConfirmed);
    const current =
      index === activeIndex && !fullyConfirmed && !(isResolvedStep && pendingConfirmation);
    const awaitingConfirm = isResolvedStep && pendingConfirmation;
    const showCheck = (done && !current) || (isResolvedStep && fullyConfirmed);

    return { done, current, awaitingConfirm, showCheck };
  };

  const trackActiveIndex = fullyConfirmed ? PROGRESS_STEPS.length : activeIndex;
  const fillClass = pendingConfirmation
    ? 'csk-stepper-track-fill--amber'
    : 'csk-stepper-track-fill';

  return (
    <ol className="csk-stepper-compact w-full">
      <div className="flex w-full items-start">
        {PROGRESS_STEPS.map((step, index) => {
          const { done, current, awaitingConfirm, showCheck } = getStepMeta(index);
          const segmentFilled = index < trackActiveIndex;
          const animateDrop = segmentFilled && index === trackActiveIndex - 1;

          return (
            <li key={step.key} className="flex min-w-0 flex-1 flex-col items-center">
              <div className="flex w-full items-center">
                {index > 0 ? (
                  <HorizontalStepperConnector
                    filled={index - 1 < trackActiveIndex}
                    fillClass={fillClass}
                    animateDrop={
                      index - 1 === trackActiveIndex - 1
                        ? `${PROGRESS_STEPS[index - 1].key}-${trackActiveIndex}`
                        : null
                    }
                  />
                ) : (
                  <div className="flex-1" aria-hidden />
                )}
                <span
                  className={`relative z-10 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold ${
                    awaitingConfirm
                      ? 'bg-amber-500 text-white ring-2 ring-amber-100'
                      : done
                        ? current
                          ? 'bg-sky-500 text-white ring-2 ring-sky-100 csk-stepper-dot-current'
                          : 'bg-sky-500 text-white'
                        : 'bg-sky-100 text-sky-700 ring-1 ring-sky-200'
                  }`}
                >
                  {showCheck ? <Check className="h-2.5 w-2.5" /> : index + 1}
                </span>
                {index < PROGRESS_STEPS.length - 1 ? (
                  <HorizontalStepperConnector
                    filled={index < trackActiveIndex}
                    fillClass={fillClass}
                    animateDrop={animateDrop ? `${step.key}-${trackActiveIndex}` : null}
                  />
                ) : (
                  <div className="flex-1" aria-hidden />
                )}
              </div>
              <span
                className={`mt-1 max-w-full truncate px-0.5 text-center text-[10px] font-medium leading-tight ${
                  awaitingConfirm
                    ? 'text-amber-700'
                    : current
                      ? 'text-sky-700'
                      : done
                        ? 'text-slate-600'
                        : 'text-slate-400'
                }`}
                title={step.label + (awaitingConfirm ? ' (confirm)' : '')}
              >
                {step.label}
                {awaitingConfirm ? '*' : ''}
              </span>
            </li>
          );
        })}
      </div>
      {showTicketAsWorkOrder(ticket) ? (
        <p className="mt-2 text-center text-[10px] font-semibold uppercase tracking-wide text-teal-700">
          Work order {ticket.workOrderId}
        </p>
      ) : null}
    </ol>
  );
}

function WorkOrderProgressStepper({ workOrder }) {
  const activeIndex = getWorkOrderProgressIndex(workOrder);

  const getStepMeta = (index) => {
    const done = index <= activeIndex;
    const current = index === activeIndex;
    return { done, current, showCheck: done && !current };
  };

  const fillClass = 'csk-stepper-track-fill--teal';

  return (
    <ol className="csk-stepper-compact w-full">
      <div className="flex w-full items-start">
        {WORK_ORDER_STEPS.map((step, index) => {
          const { done, current, showCheck } = getStepMeta(index);
          const animateDrop = index === activeIndex - 1;

          return (
            <li key={step.key} className="flex min-w-0 flex-1 flex-col items-center">
              <div className="flex w-full items-center">
                {index > 0 ? (
                  <HorizontalStepperConnector
                    filled={index - 1 < activeIndex}
                    fillClass={fillClass}
                    animateDrop={
                      index - 1 === activeIndex - 1
                        ? `${WORK_ORDER_STEPS[index - 1].key}-${activeIndex}`
                        : null
                    }
                  />
                ) : (
                  <div className="flex-1" aria-hidden />
                )}
                <span
                  className={`relative z-10 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold ${
                    done
                      ? current
                        ? 'bg-teal-600 text-white ring-2 ring-teal-100 csk-stepper-dot-current'
                        : 'bg-teal-500 text-white'
                      : 'bg-teal-100 text-teal-700 ring-1 ring-teal-200'
                  }`}
                >
                  {showCheck ? <Check className="h-2.5 w-2.5" /> : index + 1}
                </span>
                {index < WORK_ORDER_STEPS.length - 1 ? (
                  <HorizontalStepperConnector
                    filled={index < activeIndex}
                    fillClass={fillClass}
                    animateDrop={animateDrop ? `${step.key}-${activeIndex}` : null}
                  />
                ) : (
                  <div className="flex-1" aria-hidden />
                )}
              </div>
              <span
                className={`mt-1 max-w-full truncate px-0.5 text-center text-[10px] font-medium leading-tight ${
                  current ? 'text-teal-700' : done ? 'text-slate-600' : 'text-slate-400'
                }`}
                title={step.label}
              >
                {step.label}
              </span>
            </li>
          );
        })}
      </div>
    </ol>
  );
}

function NotificationPanel({
  notifications,
  onMarkRead,
  onMarkAllRead,
  onClose,
}) {
  const unread = notifications.filter((n) => !n.read).length;

  return (
    <div className="absolute right-0 top-full z-50 mt-2 w-[min(100vw-2rem,22rem)] rounded-xl border border-slate-200 bg-white shadow-lg">
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
        <div>
          <p className="font-semibold text-slate-900">Notifications</p>
          <p className="text-xs text-slate-500">{unread} unread</p>
        </div>
        <div className="flex items-center gap-1">
          {unread > 0 ? (
            <button
              type="button"
              onClick={onMarkAllRead}
              className="rounded-md px-2 py-1 text-xs font-medium text-sky-600 hover:bg-sky-50"
            >
              Mark all read
            </button>
          ) : null}
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            aria-label="Close notifications"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
      <ul className="max-h-80 overflow-y-auto py-1">
        {notifications.length === 0 ? (
          <li className="px-4 py-8 text-center text-sm text-slate-500">No notifications yet.</li>
        ) : (
          notifications.map((n) => (
            <li key={n.id}>
              <button
                type="button"
                onClick={() => onMarkRead(n.id)}
                className={`w-full px-4 py-3 text-left transition-colors hover:bg-slate-50 ${
                  !n.read ? 'bg-sky-50/60' : ''
                }`}
              >
                <div className="flex items-start gap-2">
                  {!n.read ? (
                    <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-sky-500" aria-hidden />
                  ) : (
                    <span className="mt-1.5 h-2 w-2 shrink-0" aria-hidden />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-900">{n.title}</p>
                    <p className="mt-0.5 text-sm text-slate-600">{n.message}</p>
                    <p className="mt-1 text-xs text-slate-400">{formatTimestamp(n.createdAt)}</p>
                  </div>
                </div>
              </button>
            </li>
          ))
        )}
      </ul>
    </div>
  );
}

function StarRatingInput({ label, value, onChange }) {
  return (
    <div>
      <p className="mb-2 text-sm font-medium text-slate-700">{label}</p>
      <div className="flex items-center gap-1" role="radiogroup" aria-label={label}>
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            role="radio"
            aria-checked={value === star}
            onClick={() => onChange(star)}
            className="rounded p-0.5 transition hover:scale-110 focus:outline-none focus:ring-2 focus:ring-sky-500/30"
          >
            <Star
              className={`h-7 w-7 ${
                star <= (value || 0) ? 'fill-amber-400 text-amber-400' : 'text-slate-300'
              }`}
            />
          </button>
        ))}
      </div>
      {value ? <p className="mt-1 text-xs text-slate-500">{value} out of 5 stars</p> : null}
    </div>
  );
}

function ClientFeedbackSection({ ticket, onSubmit, onViewAllTracking }) {
  const [confirmed, setConfirmed] = useState(false);
  const [serviceRating, setServiceRating] = useState(0);
  const [comment, setComment] = useState('');
  const [error, setError] = useState('');

  if (ticket.clientConfirmed) {
    return (
      <section className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50/60 p-4">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold text-emerald-900">Resolved & confirmed</h3>
            <p className="mt-1 text-sm text-emerald-800">Thank you — your service rating has been recorded.</p>
          </div>
        </div>
        <dl className="mt-4 space-y-3 text-sm">
          <div>
            <dt className="text-emerald-700">Service rating</dt>
            <dd className="mt-1 flex items-center gap-1">{renderStars(ticket.serviceRating)}</dd>
          </div>
          {ticket.feedbackComment ? (
            <div>
              <dt className="text-emerald-700">Your comment</dt>
              <dd className="mt-1 text-emerald-900">{ticket.feedbackComment}</dd>
            </div>
          ) : null}
        </dl>
        <button
          type="button"
          onClick={onViewAllTracking}
          className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-sky-700 hover:text-sky-900"
        >
          <List className="h-4 w-4" />
          View all ticket tracking
          <ChevronRight className="h-4 w-4" />
        </button>
      </section>
    );
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!confirmed) {
      setError('Please confirm that your issue has been resolved.');
      return;
    }
    if (!serviceRating) {
      setError('Please rate our service.');
      return;
    }
    setError('');
    onSubmit({
      ticketId: ticket.id,
      serviceRating,
      feedbackComment: comment.trim(),
    });
  };

  return (
    <section className="mt-4 rounded-xl border border-amber-200 bg-amber-50/60 p-4">
      <h3 className="text-sm font-semibold text-slate-900">Confirm & rate service</h3>
      <p className="mt-1 text-sm text-slate-600">
        Your ticket is resolved by our team. Confirm the fix and rate the service to complete tracking.
      </p>
      <form onSubmit={handleSubmit} className="mt-4 space-y-4">
        <label className="flex cursor-pointer items-start gap-3 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => {
              setConfirmed(e.target.checked);
              setError('');
            }}
            className="mt-0.5 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
          />
          <span>I confirm that my issue has been fully resolved</span>
        </label>
        <StarRatingInput
          label="Service rating"
          value={serviceRating}
          onChange={(v) => {
            setServiceRating(v);
            setError('');
          }}
        />
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor={`feedback-${ticket.id}`}>
            Comments (optional)
          </label>
          <textarea
            id={`feedback-${ticket.id}`}
            rows={3}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Tell us about your experience…"
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
          />
        </div>
        {error ? <p className="text-sm text-rose-600">{error}</p> : null}
        <button
          type="submit"
          className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
        >
          <CheckCircle2 className="h-4 w-4" />
          Confirm resolved & submit rating
        </button>
      </form>
    </section>
  );
}

function TicketPdfDownloadButton({ ticket, workOrder, reportTitle = 'Ticket report', compact = false }) {
  const [busy, setBusy] = useState(false);

  const handleDownload = async () => {
    setBusy(true);
    try {
      await downloadCskTicketPdf({
        ticket: {
          id: ticket.id,
          workOrderId: ticket.workOrderId,
          ticketType: ticket.ticketType,
          ticketTypeLabel: ticketTypeMeta(ticket)?.label,
          status: ticket.status,
          priority: ticket.priority,
          timelineLabel: formatTicketPriorityLabel(ticket.priority),
          created: ticket.created,
          engineer: ticket.engineer,
          client: ticket.client,
          contact: ticket.contact,
          location: ticket.location,
          coordinates: formatTicketCoordinates(ticket),
          issue: ticket.issue,
          updates: ticket.updates || [],
          clientConfirmed: ticket.clientConfirmed,
          serviceRating: ticket.serviceRating,
          feedbackComment: ticket.feedbackComment,
        },
        workOrder: workOrder
          ? {
              id: workOrder.id,
              title: workOrder.title,
              status: workOrder.status,
              engineer: workOrder.engineer,
              scheduled: workOrder.scheduled,
              zone: workOrder.zone,
              workConfirmed: workOrder.workConfirmed,
              materialsReconciled: workOrder.materialsReconciled,
              materialPlan: getWorkOrderMaterialPlan(workOrder),
              reconciledMaterials: workOrder.reconciledMaterials || [],
              sitePhotos: workOrder.sitePhotos || [],
            }
          : null,
        priorityFieldLabel: isInstallationTicket(ticket) ? 'Timeline' : 'Priority (SLA)',
        reportTitle,
      });
    } catch (err) {
      console.error('PDF download failed:', err);
      window.alert('Could not download PDF. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleDownload}
      disabled={busy}
      className={
        compact
          ? 'inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60'
          : 'inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60'
      }
      title="Download ticket as PDF"
    >
      <Download className={compact ? 'h-3.5 w-3.5' : 'h-4 w-4'} />
      {busy ? 'PDF…' : compact ? 'PDF' : 'Download PDF'}
    </button>
  );
}

function NewTicketModal({ account, onClose, onSubmit }) {
  const [ticketType, setTicketType] = useState(TICKET_TYPES[0].value);
  const [issue, setIssue] = useState('');
  const [coordinates, setCoordinates] = useState('');
  const [priority, setPriority] = useState('Major');
  const [timeline, setTimeline] = useState('24 HR');
  const [error, setError] = useState('');

  const selectedType = TICKET_TYPES.find((t) => t.value === ticketType) || TICKET_TYPES[0];
  const isInstallation = selectedType.code === 'WO';

  const handleSubmit = (e) => {
    e.preventDefault();
    const text = issue.trim();
    if (!text) {
      setError('Please describe the problem.');
      return;
    }
    const coords = coordinates.trim();
    if (!coords) {
      setError('Please enter location coordinates or a site reference.');
      return;
    }
    onSubmit({
      ticketType: selectedType.value,
      typeCode: selectedType.code,
      issue: text,
      priority: isInstallation ? timeline : priority,
      coordinates: coords,
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" role="presentation">
      <div
        className="w-full max-w-lg rounded-xl border border-slate-200 bg-white shadow-xl"
        role="dialog"
        aria-labelledby="new-ticket-title"
        aria-modal="true"
      >
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <h2 id="new-ticket-title" className="text-lg font-semibold text-slate-900">
            Open ticket
          </h2>
          <button type="button" onClick={onClose} className="rounded-md p-1 text-slate-400 hover:bg-slate-100" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4 px-5 py-4">
          <div>
            <span className="mb-2 block text-sm font-medium text-slate-700">Ticket type</span>
            <div className="grid gap-2 sm:grid-cols-2">
              {TICKET_TYPES.map((type) => (
                <label
                  key={type.value}
                  className={`flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-3 text-sm transition ${
                    ticketType === type.value
                      ? 'border-sky-500 bg-sky-50 ring-2 ring-sky-500/20'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="ticket-type"
                    value={type.value}
                    checked={ticketType === type.value}
                    onChange={() => {
                      setTicketType(type.value);
                      setError('');
                    }}
                    className="mt-0.5"
                  />
                  <span>
                    <span className="block font-semibold text-slate-900">{type.label}</span>
                    <span className="mt-0.5 block text-xs text-slate-500">
                      {type.code === 'WO'
                        ? 'New fiber installation request'
                        : 'Fault or maintenance on existing service'}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="ticket-issue">
              Describe the problem
            </label>
            <textarea
              id="ticket-issue"
              rows={4}
              value={issue}
              onChange={(e) => { setIssue(e.target.value); setError(''); }}
              placeholder={
                selectedType.code === 'WO'
                  ? 'e.g. New FTTH drop required at building entrance, 4 units…'
                  : 'e.g. ONT lights red, no internet since morning…'
              }
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="ticket-coordinates">
              Location coordinates
            </label>
            <textarea
              id="ticket-coordinates"
              rows={2}
              value={coordinates}
              onChange={(e) => { setCoordinates(e.target.value); setError(''); }}
              placeholder="e.g. -1.2921, 36.8219 · plus code · Google Maps link · landmark near site…"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
            />
            <p className="mt-1 text-xs text-slate-500">
              Enter coordinates or any location reference the field team can use.
            </p>
          </div>
          {isInstallation ? (
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="ticket-timeline">
                Timeline
              </label>
              <select
                id="ticket-timeline"
                value={timeline}
                onChange={(e) => setTimeline(e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
              >
                {TICKET_TIMELINES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.value}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-slate-500">
                At timeline, 24 HR, 7 Days, 1 Week, or 2 Weeks
              </p>
            </div>
          ) : (
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="ticket-priority">
                Priority (SLA)
              </label>
              <select
                id="ticket-priority"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
              >
                {TICKET_PRIORITIES.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.value} ({p.sla})
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-slate-500">
                Critical (3 hours), Major (3 hours), Minor (24 hours)
              </p>
            </div>
          )}
          {error ? <p className="text-sm text-rose-600">{error}</p> : null}
          <p className="text-xs text-slate-500">
            Account: {account.name} · {account.contact} · {account.address}
          </p>
          <div className="flex justify-end gap-2 border-t border-slate-100 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="inline-flex items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700"
            >
              <Plus className="h-4 w-4" />
              Open ticket
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ClientTicketDetailSidebar({
  ticket,
  workOrders,
  onClose,
  onSubmitFeedback,
  onViewAllTracking,
  onUpdateCoordinates,
}) {
  const [coordinatesDraft, setCoordinatesDraft] = useState('');
  const [coordError, setCoordError] = useState('');
  const [coordSaved, setCoordSaved] = useState(false);

  useCloseOnEscape(onClose);

  useEffect(() => {
    if (!ticket) return;
    setCoordinatesDraft(formatTicketCoordinates(ticket));
    setCoordError('');
    setCoordSaved(false);
  }, [ticket]);

  if (!ticket) return null;

  const awaitingFeedback = needsClientConfirmation(ticket);
  const displayRef = showTicketAsWorkOrder(ticket) ? ticketDisplayRef(ticket) : ticket.id;
  const linkedWorkOrder = getLinkedWorkOrder(ticket, workOrders);
  const showEngineerContact =
    ticket.engineer &&
    (ticket.status === 'In Progress' ||
      (linkedWorkOrder && isActiveWorkOrderStatus(linkedWorkOrder.status)));
  const canEditCoordinates = ticket.status === 'Open' || ticket.status === 'In Progress';

  const handleSaveCoordinates = () => {
    const text = coordinatesDraft.trim();
    if (!text) {
      setCoordError('Enter coordinates or a location reference.');
      return;
    }
    if (text === formatTicketCoordinates(ticket)) return;
    onUpdateCoordinates(ticket.id, text);
    setCoordSaved(true);
    setCoordError('');
  };

  return (
    <div className="csk-client-sidebar-root fixed inset-0 z-50 flex justify-end" role="presentation">
      <button
        type="button"
        className="csk-client-sidebar-backdrop absolute inset-0 bg-slate-900/45 backdrop-blur-[2px]"
        onClick={onClose}
        aria-label="Close ticket details"
      />
      <aside
        className="csk-client-sidebar relative z-10 flex h-full w-full max-w-lg flex-col border-l border-slate-200/80 bg-white shadow-2xl"
        role="dialog"
        aria-labelledby="client-ticket-detail-title"
        aria-modal="true"
      >
        <div className="csk-client-sidebar-header shrink-0 border-b border-slate-100 bg-gradient-to-br from-sky-50 via-white to-white px-5 py-5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-sky-600">
                {showTicketAsWorkOrder(ticket) ? 'Work order details' : 'Trouble ticket details'}
              </p>
              <h2 id="client-ticket-detail-title" className="mt-1 truncate text-xl font-bold text-slate-900">
                {displayRef}
              </h2>
              {showTicketAsWorkOrder(ticket) ? (
                <p className="mt-0.5 text-xs text-slate-500">Linked trouble ticket {ticket.id}</p>
              ) : null}
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <TimelineBadge timeline={ticket.priority} />
                <Badge label={ticket.status} map={STATUS_STYLES} />
                {showTicketAsWorkOrder(ticket) ? (
                  <span className="rounded-full bg-teal-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-teal-800">
                    WO
                  </span>
                ) : null}
                {awaitingFeedback ? (
                  <span className="rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                    Confirm service
                  </span>
                ) : null}
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <TicketPdfDownloadButton
                ticket={ticket}
                workOrder={linkedWorkOrder}
                reportTitle={showTicketAsWorkOrder(ticket) ? 'Work order report' : 'Ticket report'}
                compact
              />
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5">
          <section className="csk-client-sidebar-section">
            <h3 className="csk-client-sidebar-section-title">Progress</h3>
            <TicketProgressStepper ticket={ticket} />
          </section>

          <section className="csk-client-sidebar-section mt-5">
            <h3 className="csk-client-sidebar-section-title">Issue summary</h3>
            <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50/80 p-4">
              <p className="text-sm leading-relaxed text-slate-800">{ticket.issue}</p>
              <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Ticket type</dt>
                  <dd className="mt-0.5 font-medium text-slate-900">
                    {ticketTypeMeta(ticket)?.label || '—'}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Opened</dt>
                  <dd className="mt-0.5 font-medium text-slate-900">{ticket.created}</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    {isInstallationTicket(ticket) ? 'Timeline' : 'Priority (SLA)'}
                  </dt>
                  <dd className="mt-1">
                    <TimelineBadge timeline={ticket.priority} />
                  </dd>
                </div>
                <div className="sm:col-span-2">
                  <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Site address</dt>
                  <dd className="mt-0.5 flex items-start gap-1.5 font-medium">
                    <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
                    {ticket.location && getTicketDirectionsUrl(ticket) ? (
                      <TicketDirectionsLink
                        ticket={ticket}
                        label={ticket.location}
                        className="text-slate-900 hover:text-sky-700"
                      />
                    ) : (
                      <span className="text-slate-900">{ticket.location || '—'}</span>
                    )}
                  </dd>
                </div>
                <div className="sm:col-span-2">
                  <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Coordinates</dt>
                  {canEditCoordinates ? (
                    <dd className="mt-2 space-y-2">
                      <textarea
                        id={`client-coords-${ticket.id}`}
                        rows={2}
                        value={coordinatesDraft}
                        onChange={(e) => {
                          setCoordinatesDraft(e.target.value);
                          setCoordError('');
                          setCoordSaved(false);
                        }}
                        placeholder="Coordinates or site reference for the field team"
                        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 font-mono text-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
                      />
                      {coordinatesDraft.trim() && buildMapsDirectionsUrl(coordinatesDraft.trim()) ? (
                        <TicketDirectionsLink
                          destination={coordinatesDraft.trim()}
                          label="Get directions"
                          className="text-xs font-semibold"
                        />
                      ) : null}
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={handleSaveCoordinates}
                          disabled={coordinatesDraft.trim() === formatTicketCoordinates(ticket)}
                          className="rounded-lg bg-sky-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          Save coordinates
                        </button>
                        {coordSaved ? (
                          <span className="text-xs font-medium text-emerald-600">Updated — admin & engineer will see this</span>
                        ) : null}
                      </div>
                      {coordError ? <p className="text-xs text-rose-600">{coordError}</p> : null}
                    </dd>
                  ) : (
                    <dd className="mt-0.5 font-mono text-sm">
                      {formatTicketCoordinates(ticket) ? (
                        <TicketDirectionsLink
                          ticket={ticket}
                          className="font-mono text-sm font-medium"
                        />
                      ) : (
                        <span className="font-medium text-slate-900">—</span>
                      )}
                    </dd>
                  )}
                </div>
              </dl>
            </div>
          </section>

          {showEngineerContact ? (
            <section className="csk-client-sidebar-section mt-5">
              <AssignedEngineerContactCard
                engineerName={ticket.engineer}
                workOrderStatus={linkedWorkOrder?.status}
                variant="client"
              />
            </section>
          ) : null}

          {linkedWorkOrder ? (
            <WorkOrderMaterialsSummary workOrder={linkedWorkOrder} />
          ) : null}

          {ticket.status === 'Resolved' ? (
            <ClientFeedbackSection
              ticket={ticket}
              onSubmit={onSubmitFeedback}
              onViewAllTracking={onViewAllTracking}
            />
          ) : null}

          <section className="csk-client-sidebar-section mt-5">
            <h3 className="csk-client-sidebar-section-title">Activity timeline</h3>
            <ol className="csk-client-timeline mt-4 space-y-0">
              {(ticket.updates || []).map((u, i) => (
                <li key={`${ticket.id}-sidebar-u-${i}`} className="csk-client-timeline-item flex gap-3 pb-4 text-sm last:pb-0">
                  <span className="csk-client-timeline-dot mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full bg-sky-400 ring-4 ring-sky-100" />
                  <div className="min-w-0 flex-1 rounded-lg border border-slate-100 bg-white px-3 py-2.5 shadow-sm">
                    <p className="text-slate-800">{u.message}</p>
                    <p className="mt-1 text-xs text-slate-500">
                      {formatTimestamp(u.at)} · {u.by}
                    </p>
                  </div>
                </li>
              ))}
              {(ticket.updates || []).length === 0 ? (
                <li className="text-sm text-slate-500">No activity yet.</li>
              ) : null}
            </ol>
          </section>
        </div>
      </aside>
    </div>
  );
}

function ClientView({
  tickets,
  workOrders,
  account,
  notifications,
  onCreateTicket,
  onUpdateCoordinates,
  onMarkNotificationRead,
  onMarkAllNotificationsRead,
  onSubmitFeedback,
}) {
  const [showForm, setShowForm] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const ticketsSectionRef = useRef(null);

  const mine = useMemo(
    () => tickets.filter((t) => t.client === account.name).sort((a, b) => b.created.localeCompare(a.created)),
    [tickets, account.name]
  );

  const filteredMine = useMemo(() => {
    if (!statusFilter) return mine;
    return mine.filter((t) => t.status === statusFilter);
  }, [mine, statusFilter]);

  const toggleStatusFilter = (status) => {
    setStatusFilter((current) => (current === status ? null : status));
    setExpandedId(null);
  };

  const clientNotifications = useMemo(
    () =>
      notifications
        .filter((n) => n.client === account.name)
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
    [notifications, account.name]
  );

  const unreadCount = clientNotifications.filter((n) => !n.read).length;

  const openCount = mine.filter((t) => t.status === 'Open').length;
  const activeCount = mine.filter((t) => t.status === 'In Progress').length;
  const resolvedCount = mine.filter((t) => t.status === 'Resolved').length;
  const pendingFeedbackCount = mine.filter((t) => needsClientConfirmation(t)).length;

  const handleResolvedClick = () => {
    toggleStatusFilter('Resolved');
  };

  const handleViewAllTracking = () => {
    setStatusFilter(null);
    setExpandedId(null);
    ticketsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const selectedTicket = useMemo(
    () => mine.find((t) => t.id === expandedId) || null,
    [mine, expandedId]
  );

  const handleTicketOpen = (ticketId) => {
    setExpandedId((current) => (current === ticketId ? null : ticketId));
  };

  return (
    <div className="csk-client-view space-y-6">
      <section className="csk-client-hero">
        <div className="csk-client-hero-inner">
          <RoleViewBanner role="Client" />
          <div className="csk-client-hero-bar mt-4 flex flex-wrap items-center justify-between gap-3">
            <div className="csk-client-account-card">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-sky-600 text-white shadow-md shadow-sky-200">
                  <User className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-900">{account.name}</p>
                  <p className="text-xs text-slate-500">{account.accountId} · {account.plan}</p>
                  <p className="mt-0.5 flex items-center gap-1 text-xs text-slate-500">
                    <MapPin className="h-3 w-3" /> {account.address}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowNotifications((v) => !v)}
                  className="csk-client-action-btn relative inline-flex items-center gap-2"
                  aria-expanded={showNotifications}
                  aria-haspopup="true"
                >
                  <Bell className="h-4 w-4" />
                  Notifications
                  {unreadCount > 0 ? (
                    <span className="flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-rose-500 px-1.5 text-xs font-semibold text-white">
                      {unreadCount}
                    </span>
                  ) : null}
                </button>
                {showNotifications ? (
                  <NotificationPanel
                    notifications={clientNotifications}
                    onMarkRead={(id) => {
                      onMarkNotificationRead(id);
                    }}
                    onMarkAllRead={onMarkAllNotificationsRead}
                    onClose={() => setShowNotifications(false)}
                  />
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => setShowForm(true)}
                className="csk-client-primary-btn inline-flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Open ticket
              </button>
            </div>
          </div>
        </div>
      </section>

      <div className="csk-client-stats grid gap-4 sm:grid-cols-3">
        <StatCard
          label="Open"
          value={openCount}
          icon={AlertCircle}
          accent="bg-sky-50 text-sky-600"
          isActive={statusFilter === 'Open'}
          onClick={() => toggleStatusFilter('Open')}
        />
        <StatCard
          label="In progress"
          value={activeCount}
          icon={Clock}
          accent="bg-amber-50 text-amber-600"
          isActive={statusFilter === 'In Progress'}
          onClick={() => toggleStatusFilter('In Progress')}
        />
        <StatCard
          label="Resolved"
          value={resolvedCount}
          icon={CheckCircle2}
          accent="bg-emerald-50 text-emerald-600"
          isActive={statusFilter === 'Resolved'}
          hasAlert={pendingFeedbackCount > 0}
          alertCount={pendingFeedbackCount}
          alertHint="Confirm resolved tickets"
          onClick={handleResolvedClick}
        />
      </div>

      <section className="csk-client-tickets-section rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm sm:p-5">
      <div ref={ticketsSectionRef} id="client-ticket-tracking" className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-4">
        <div>
          <h2 className="text-lg font-bold text-slate-900">
            {statusFilter === 'In Progress' || statusFilter === 'Resolved'
              ? 'My work orders'
              : 'My trouble tickets'}
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            {statusFilter === 'In Progress'
              ? 'Active work orders linked to your trouble tickets'
              : statusFilter === 'Resolved'
                ? 'Completed work orders — confirm service when ready'
                : statusFilter
                  ? `Showing ${statusFilter} tickets only`
                  : null}
          </p>
        </div>
      </div>

      <div className="csk-client-ticket-list mt-4 space-y-3">
        {filteredMine.map((t) => {
          const isSelected = expandedId === t.id;
          const awaitingFeedback = needsClientConfirmation(t);
          return (
            <article
              key={t.id}
              className={`csk-client-ticket-card group cursor-pointer rounded-xl border bg-white transition-all ${
                isSelected
                  ? 'border-sky-400 bg-sky-50/40 ring-2 ring-sky-200 shadow-md'
                  : awaitingFeedback
                    ? 'border-amber-300 ring-1 ring-amber-200 hover:border-amber-400 hover:shadow-md'
                    : 'border-slate-200 hover:border-sky-200 hover:shadow-md'
              }`}
              role="button"
              tabIndex={0}
              onClick={() => handleTicketOpen(t.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleTicketOpen(t.id);
                }
              }}
              aria-pressed={isSelected}
            >
              <div className="px-4 py-4 sm:px-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-semibold text-slate-900">
                        {showTicketAsWorkOrder(t) ? ticketDisplayRef(t) : t.id}
                      </p>
                      {ticketTypeMeta(t)?.code ? (
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                          ticketTypeMeta(t)?.code === 'WO'
                            ? 'bg-teal-100 text-teal-800'
                            : 'bg-violet-100 text-violet-800'
                        }`}>
                          {ticketTypeMeta(t).code}
                        </span>
                      ) : null}
                      <TimelineBadge timeline={t.priority} />
                      <Badge label={t.status} map={STATUS_STYLES} />
                      {awaitingFeedback ? (
                        <span className="rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                          Confirm service
                        </span>
                      ) : null}
                      {t.clientConfirmed ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-800">
                          <Check className="h-3 w-3" />
                          Confirmed
                        </span>
                      ) : null}
                      {t.clientConfirmed ? (
                        <span className="inline-flex items-center gap-0.5 text-amber-500">
                          {renderStars(t.serviceRating, 'h-3.5 w-3.5')}
                        </span>
                      ) : null}
                    </div>
                    <p className="mt-1.5 line-clamp-2 text-sm text-slate-700">{t.issue}</p>
                    <p className="mt-1.5 text-xs text-slate-500">
                      {ticketTypeMeta(t)?.label || t.category} · Opened {t.created}
                      {formatTicketCoordinates(t) ? (
                        <>
                          {' · '}
                          <TicketDirectionsLink ticket={t} className="text-xs" />
                        </>
                      ) : null}
                      {showTicketAsWorkOrder(t) && t.id !== ticketDisplayRef(t) ? ` · Ref ${t.id}` : ''}
                      {t.engineer ? ` · Engineer: ${t.engineer}` : ''}
                    </p>
                  </div>
                  <span className="csk-client-ticket-open inline-flex items-center gap-1 rounded-lg bg-slate-100 px-2.5 py-1.5 text-xs font-medium text-slate-600 transition group-hover:bg-sky-100 group-hover:text-sky-700">
                    Details
                    <ChevronRight className="h-4 w-4" />
                  </span>
                </div>
              </div>
            </article>
          );
        })}
        {filteredMine.length === 0 ? (
          <p className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-10 text-center text-sm text-slate-500">
            {mine.length === 0
              ? 'No tickets yet. Click "Open ticket" to log a new request.'
              : statusFilter
                ? `No ${statusFilter.toLowerCase()} tickets. Click the card again to show all.`
                : 'No tickets match this filter.'}
          </p>
        ) : null}
      </div>
      </section>

      {selectedTicket ? (
        <ClientTicketDetailSidebar
          ticket={selectedTicket}
          workOrders={workOrders}
          onClose={() => setExpandedId(null)}
          onSubmitFeedback={onSubmitFeedback}
          onViewAllTracking={handleViewAllTracking}
          onUpdateCoordinates={onUpdateCoordinates}
        />
      ) : null}

      {showForm ? (
        <NewTicketModal
          account={account}
          onClose={() => setShowForm(false)}
          onSubmit={onCreateTicket}
        />
      ) : null}
    </div>
  );
}

function CompletedWorkOrderCard({
  workOrder,
  ticket,
  onClick,
  onSubmitDelayReason,
  engineerMode = false,
}) {
  const delayed = isWorkOrderDelayed(workOrder, ticket);
  const dueAt = getWorkOrderSlaDueAt(workOrder, ticket);
  const completedAt = parseWorkOrderDate(workOrder.completedAt);
  const [reason, setReason] = useState(workOrder.delayReason || '');
  const [error, setError] = useState('');

  const handleDelaySubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const trimmed = reason.trim();
    if (!trimmed) {
      setError('Please explain why this work order was delayed.');
      return;
    }
    setError('');
    onSubmitDelayReason?.({ workOrderId: workOrder.id, delayReason: trimmed });
  };

  return (
    <article
      className={`csk-completion-card ${delayed ? 'is-delayed' : 'is-ontime'} ${onClick ? 'is-clickable' : ''}`}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="font-semibold text-slate-900">{workOrder.id}</p>
          <p className="text-xs text-slate-500">TT {workOrder.ticketId}</p>
        </div>
        <span
          className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
            delayed ? 'bg-rose-100 text-rose-800' : 'bg-emerald-100 text-emerald-800'
          }`}
        >
          {delayed ? 'Delayed' : 'On time'}
        </span>
      </div>
      <p className="mt-2 line-clamp-2 text-sm text-slate-700">{workOrder.title}</p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {ticket ? <TimelineBadge timeline={ticket.priority} /> : null}
        <Badge label={workOrder.status} map={STATUS_STYLES} />
      </div>
      <dl className="mt-3 grid gap-2 text-xs text-slate-600">
        <div className="flex justify-between gap-2">
          <dt className="text-slate-500">SLA due</dt>
          <dd className="font-medium text-slate-800">{formatSlaDateTime(dueAt)}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="text-slate-500">Completed</dt>
          <dd className="font-medium text-slate-800">{formatSlaDateTime(completedAt)}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="text-slate-500">Engineer</dt>
          <dd className="font-medium text-slate-800">{workOrder.engineer}</dd>
        </div>
      </dl>
      {delayed && workOrder.delayReason ? (
        <p className="mt-3 rounded-lg border border-rose-100 bg-rose-50/60 px-3 py-2 text-xs text-rose-900">
          <span className="font-semibold">Delay reason: </span>
          {workOrder.delayReason}
        </p>
      ) : null}
      {engineerMode && delayed && !workOrder.delayReason && onSubmitDelayReason ? (
        <form onSubmit={handleDelaySubmit} className="mt-3 space-y-2" onClick={(e) => e.stopPropagation()}>
          <label className="block text-xs font-semibold text-rose-800" htmlFor={`delay-${workOrder.id}`}>
            Reason for delay (required)
          </label>
          <textarea
            id={`delay-${workOrder.id}`}
            rows={2}
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              setError('');
            }}
            placeholder="Explain why the work exceeded the SLA window…"
            className="w-full rounded-lg border border-rose-200 bg-white px-3 py-2 text-xs outline-none focus:border-rose-400 focus:ring-2 focus:ring-rose-200"
          />
          {error ? <p className="text-xs text-rose-600">{error}</p> : null}
          <button
            type="submit"
            className="inline-flex w-full items-center justify-center rounded-lg bg-rose-600 px-3 py-2 text-xs font-semibold text-white hover:bg-rose-700"
          >
            Submit delay reason
          </button>
        </form>
      ) : null}
    </article>
  );
}

function CompletionSplitBoard({
  pairs,
  onCardClick,
  onSubmitDelayReason,
  engineerMode = false,
  layout = 'equal',
  onTimeTitle = 'On time completion',
  delayedTitle = 'Delayed work orders',
  onTimeHint = 'Completed within priority SLA window',
  delayedHint = 'Completed after the SLA deadline',
}) {
  const { onTime, delayed } = splitWorkOrdersBySla(pairs);

  const renderColumn = (title, hint, items, tone) => (
    <section className={`csk-completion-panel csk-completion-panel--${tone}`}>
      <div className="csk-completion-panel-head">
        <div>
          <h3 className="text-base font-bold text-slate-900">{title}</h3>
          <p className="mt-0.5 text-xs text-slate-500">{hint}</p>
        </div>
        <span className={`csk-completion-count csk-completion-count--${tone}`}>{items.length}</span>
      </div>
      <div className="csk-completion-panel-body space-y-3">
        {items.length === 0 ? (
          <p className="rounded-lg border border-dashed border-slate-200 bg-white/70 px-4 py-8 text-center text-sm text-slate-500">
            No work orders in this group yet.
          </p>
        ) : (
          items.map(({ workOrder, ticket }) => (
            <CompletedWorkOrderCard
              key={workOrder.id}
              workOrder={workOrder}
              ticket={ticket}
              engineerMode={engineerMode}
              onSubmitDelayReason={onSubmitDelayReason}
              onClick={onCardClick ? () => onCardClick(workOrder, ticket) : undefined}
            />
          ))
        )}
      </div>
    </section>
  );

  return (
    <div className={`csk-completion-split ${layout === 'dashboard' ? 'csk-completion-split--dashboard' : 'grid gap-4 lg:grid-cols-2'}`}>
      {renderColumn(onTimeTitle, onTimeHint, onTime, 'ontime')}
      {renderColumn(delayedTitle, delayedHint, delayed, 'delayed')}
    </div>
  );
}

function AdminTicketReviewSidebar({
  ticket,
  workOrders,
  onClose,
  onIssueWorkOrder,
}) {
  const existingWo = workOrders.find((w) => w.ticketId === ticket.id);
  const plannedWoId = workOrderIdFromTicketId(ticket.id);
  const [engineer, setEngineer] = useState(ticket.engineer || '');
  const [woTitle, setWoTitle] = useState(
    existingWo?.title || `${ticketTypeMeta(ticket)?.value || ticket.category || 'Field visit'} — ${ticket.location}`
  );
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleIssue = (e) => {
    e.preventDefault();
    if (existingWo) return;
    if (!engineer) {
      setError('Select a team leader / engineer to assign.');
      return;
    }
    const title = woTitle.trim();
    if (!title) {
      setError('Enter a work order task description.');
      return;
    }
    setError('');
    setSubmitting(true);
    onIssueWorkOrder({
      ticketId: ticket.id,
      engineer,
      title,
      scheduled: defaultWorkOrderSchedule(),
      zone: ENGINEERS.find((en) => en.name === engineer)?.zone || inferZoneFromLocation(ticket.location),
    });
    setSubmitting(false);
    onClose();
  };

  useCloseOnEscape(onClose);

  return (
    <div className="fixed inset-0 z-50 flex justify-end" role="presentation">
      <button
        type="button"
        className="csk-admin-sidebar-backdrop absolute inset-0 bg-slate-900/45 backdrop-blur-[2px]"
        onClick={onClose}
        aria-label="Close ticket review panel"
      />
      <aside
        className="csk-admin-sidebar relative z-10 flex h-full w-full max-w-md flex-col border-l border-slate-200/80 bg-white shadow-2xl"
        role="dialog"
        aria-labelledby="admin-ticket-review-title"
        aria-modal="true"
      >
        <div className="csk-admin-sidebar-header shrink-0 border-b border-slate-100 bg-gradient-to-br from-violet-50 via-white to-white px-5 py-5">
          <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-violet-600">
              {existingWo || ticket.workOrderId ? 'Work order control' : 'Ticket review'}
            </p>
            <h2 id="admin-ticket-review-title" className="mt-1 text-xl font-bold text-slate-900">
              {existingWo?.id || ticket.workOrderId || ticket.id}
            </h2>
            {(existingWo || ticket.workOrderId) ? (
              <p className="mt-0.5 text-xs text-slate-500">From trouble ticket {ticket.id}</p>
            ) : null}
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <TimelineBadge timeline={ticket.priority} />
              <Badge label={ticket.status} map={STATUS_STYLES} />
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <TicketPdfDownloadButton
              ticket={ticket}
              workOrder={existingWo}
              reportTitle={existingWo || ticket.workOrderId ? 'Work order report' : 'Ticket report'}
              compact
            />
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          <section className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <h3 className="text-sm font-semibold text-slate-900">Client details</h3>
            <dl className="mt-3 space-y-3 text-sm">
              <div className="flex items-start gap-3">
                <User className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                <div>
                  <dt className="text-slate-500">Client</dt>
                  <dd className="font-medium text-slate-900">{ticket.client}</dd>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Phone className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                <div>
                  <dt className="text-slate-500">Contact</dt>
                  <dd className="font-medium text-slate-900">{ticket.contact}</dd>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                <div>
                  <dt className="text-slate-500">Site address</dt>
                  <dd className="font-medium">
                    {ticket.location && getTicketDirectionsUrl(ticket) ? (
                      <TicketDirectionsLink ticket={ticket} label={ticket.location} />
                    ) : (
                      <span className="text-slate-900">{ticket.location || '—'}</span>
                    )}
                  </dd>
                  {formatTicketCoordinates(ticket) ? (
                    <dd className="mt-1 font-mono text-xs">
                      <TicketDirectionsLink ticket={ticket} className="text-xs" />
                    </dd>
                  ) : null}
                </div>
              </div>
              <div className="flex items-start gap-3">
                <FileText className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                <div>
                  <dt className="text-slate-500">Ticket type</dt>
                  <dd className="font-medium text-slate-900">{ticketTypeMeta(ticket)?.label || '—'}</dd>
                </div>
              </div>
            </dl>
          </section>

          <section className="mt-4">
            <h3 className="text-sm font-semibold text-slate-900">Issue reported</h3>
            <p className="mt-2 text-sm text-slate-700">{ticket.issue}</p>
            <p className="mt-2 text-xs text-slate-500">
              {ticketTypeMeta(ticket)?.label || 'Ticket'} · Logged {ticket.created}
            </p>
          </section>

          <section className="mt-4">
            <h3 className="text-sm font-semibold text-slate-900">Activity timeline</h3>
            {(ticket.updates || []).length > 0 ? (
              <ol className="mt-3 space-y-3">
                {(ticket.updates || []).map((u, i) => (
                  <li key={`${ticket.id}-sidebar-u-${i}`} className="flex gap-3 text-sm">
                    <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-violet-400" />
                    <div>
                      <p className="text-slate-800">{u.message}</p>
                      <p className="text-xs text-slate-500">
                        {formatTimestamp(u.at)} · {u.by}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            ) : (
              <p className="mt-2 text-sm text-slate-500">No activity yet.</p>
            )}
          </section>

          {existingWo ? <WorkOrderMaterialsSummary workOrder={existingWo} variant="admin" /> : null}

          {ticket.clientConfirmed ? (
            <section className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50/60 p-4">
              <h3 className="text-sm font-semibold text-emerald-900">Client confirmation</h3>
              <p className="mt-1 text-sm text-emerald-800">Client confirmed this ticket as resolved.</p>
              <div className="mt-3 flex items-center gap-1">{renderStars(ticket.serviceRating)}</div>
              {ticket.feedbackComment ? (
                <p className="mt-2 text-sm text-emerald-900">{ticket.feedbackComment}</p>
              ) : null}
            </section>
          ) : isPendingClientConfirm(ticket) ? (
            <section className="mt-4 rounded-xl border border-amber-200 bg-amber-50/60 p-4 text-sm text-amber-900">
              Work completed — awaiting client confirmation and service rating.
            </section>
          ) : null}

          <section className="mt-6 rounded-xl border border-violet-200 bg-violet-50/50 p-4">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-violet-600" />
              <h3 className="text-sm font-semibold text-slate-900">Assign team leader & issue work order</h3>
            </div>

            {existingWo ? (
              <div className="mt-3 space-y-3">
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
                  <p className="font-medium">Work order already issued</p>
                  <p className="mt-1">
                    {existingWo.id} · {existingWo.engineer} · {existingWo.status}
                  </p>
                  <p className="mt-1 text-emerald-800">{existingWo.title}</p>
                </div>
                {isActiveWorkOrderStatus(existingWo.status) && existingWo.engineer ? (
                  <AssignedEngineerContactCard
                    engineerName={existingWo.engineer}
                    workOrderStatus={existingWo.status}
                    variant="admin"
                  />
                ) : null}
              </div>
            ) : (
              <form onSubmit={handleIssue} className="mt-4 space-y-4">
                <p className="rounded-lg border border-violet-200 bg-violet-100/50 px-3 py-2 text-xs text-violet-900">
                  Trouble ticket <strong>{ticket.id}</strong> will be issued as work order{' '}
                  <strong>{plannedWoId}</strong> (same reference number).
                </p>
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="assign-engineer">
                    Team leader (Engineer)
                  </label>
                  <select
                    id="assign-engineer"
                    value={engineer}
                    onChange={(e) => {
                      setEngineer(e.target.value);
                      setError('');
                    }}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20"
                  >
                    <option value="">Select engineer…</option>
                    {ENGINEERS.map((en) => (
                      <option key={en.name} value={en.name}>
                        {en.name} — {en.role} ({en.zone})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="wo-title">
                    Work order task
                  </label>
                  <textarea
                    id="wo-title"
                    rows={3}
                    value={woTitle}
                    onChange={(e) => {
                      setWoTitle(e.target.value);
                      setError('');
                    }}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20"
                  />
                </div>
                {error ? <p className="text-sm text-rose-600">{error}</p> : null}
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-violet-700 disabled:opacity-60"
                >
                  <Wrench className="h-4 w-4" />
                  Issue work order to engineer
                </button>
              </form>
            )}
          </section>
        </div>
      </aside>
    </div>
  );
}

function AdminView({
  tickets,
  workOrders,
  notifications,
  unseenOpenTicketIds = [],
  unseenResolvedTicketIds = [],
  onAcknowledgeOpenAlerts,
  onAcknowledgeResolvedAlerts,
  onTicketReview,
  onIssueWorkOrder,
  onMarkNotificationRead,
  onMarkAllNotificationsRead,
}) {
  const [statusFilter, setStatusFilter] = useState(null);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [showNotifications, setShowNotifications] = useState(false);

  const adminNotifications = useMemo(
    () =>
      notifications
        .filter((n) => n.admin)
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
    [notifications]
  );
  const unreadAdminCount = adminNotifications.filter((n) => !n.read).length;

  const selectedTicket = useMemo(
    () => tickets.find((t) => t.id === selectedTicketId) || null,
    [tickets, selectedTicketId]
  );

  const openCount = tickets.filter((t) => t.status === 'Open').length;
  const hasOpenAlert = unseenOpenTicketIds.length > 0;
  const inProgressCount = tickets.filter((t) => isAdminInProgress(t)).length;
  const resolvedCount = tickets.filter((t) => isAdminResolved(t)).length;
  const hasResolvedAlert = unseenResolvedTicketIds.length > 0;
  const activeWo = workOrders.filter((w) => w.status !== 'Closed').length;

  const filteredTickets = useMemo(() => {
    if (statusFilter === 'Open') return tickets.filter((t) => t.status === 'Open');
    if (statusFilter === 'In Progress') return tickets.filter((t) => isAdminInProgress(t));
    if (statusFilter === 'Resolved') return tickets.filter((t) => isAdminResolved(t));
    return tickets;
  }, [tickets, statusFilter]);

  const resolvedCompletionPairs = useMemo(
    () => getResolvedCompletedWorkOrders(workOrders, tickets, { adminConfirmedOnly: true }),
    [workOrders, tickets]
  );

  const showWoCentricView = statusFilter === 'In Progress' || statusFilter === 'Resolved';
  const showResolvedSplit = statusFilter === 'Resolved';
  const showTeamView = statusFilter === 'Technical Team';
  const showReportView = statusFilter === 'Report';

  const awaitingClientCount = tickets.filter((t) => isPendingClientConfirm(t)).length;
  const teamAvailableCount = useMemo(
    () =>
      TECHNICAL_TEAM.filter(
        (member) => getTeamMemberAvailability(member.name, workOrders) === 'Available'
      ).length,
    [workOrders]
  );
  const teamRankCounts = useMemo(
    () => ({
      leaders: TECHNICAL_TEAM.filter((m) => m.rank === 'Team Leader').length,
      engineers: TECHNICAL_TEAM.filter((m) => m.rank === 'Engineer').length,
      technicians: TECHNICAL_TEAM.filter((m) => m.rank === 'Technician').length,
    }),
    []
  );
  const slaSplit = useMemo(
    () => splitWorkOrdersBySla(resolvedCompletionPairs),
    [resolvedCompletionPairs]
  );

  const toggleStatusFilter = (status) => {
    setStatusFilter((current) => (current === status ? null : status));
  };

  const handleOpenTicketsClick = () => {
    toggleStatusFilter('Open');
    if (hasOpenAlert) {
      onAcknowledgeOpenAlerts?.();
    }
  };

  const handleResolvedClick = () => {
    toggleStatusFilter('Resolved');
    if (hasResolvedAlert) {
      onAcknowledgeResolvedAlerts?.();
    }
  };

  const handleTicketClick = (ticket) => {
    setSelectedTicketId((current) => {
      if (current === ticket.id) return null;
      onTicketReview?.(ticket.id);
      return ticket.id;
    });
  };

  const handleExportExcel = () => {
    exportAdminTicketsToExcel(tickets, workOrders);
  };

  return (
    <div className="csk-admin-dashboard">
      <div className="csk-admin-dashboard-top">
        <RoleViewBanner role="Admin" />
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={downloadCskUserManual}
            className="csk-admin-action-btn inline-flex items-center gap-2"
            title="Download Customer Services Kenya user manual (PDF)"
          >
            <FileText className="h-4 w-4" aria-hidden />
            User manual
          </button>
          <button
            type="button"
            onClick={handleExportExcel}
            className="csk-admin-action-btn inline-flex items-center gap-2"
          >
            <Download className="h-4 w-4" aria-hidden />
            Export Excel
          </button>
          <div className="relative shrink-0">
          <button
            type="button"
            onClick={() => setShowNotifications((v) => !v)}
            className="csk-admin-action-btn relative inline-flex items-center gap-2"
            aria-expanded={showNotifications}
            aria-haspopup="true"
          >
            <Bell className="h-4 w-4" />
            Alerts
            {unreadAdminCount > 0 ? (
              <span className="flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-violet-600 px-1.5 text-xs font-semibold text-white">
                {unreadAdminCount}
              </span>
            ) : null}
          </button>
          {showNotifications ? (
            <NotificationPanel
              notifications={adminNotifications}
              onMarkRead={onMarkNotificationRead}
              onMarkAllRead={onMarkAllNotificationsRead}
              onClose={() => setShowNotifications(false)}
            />
          ) : null}
          </div>
        </div>
      </div>

      <div className="csk-admin-metrics-grid">
        <StatCard
          compact
          label="Open tickets"
          value={openCount}
          icon={AlertCircle}
          accent="bg-rose-50 text-rose-600"
          isActive={statusFilter === 'Open'}
          hasAlert={hasOpenAlert}
          alertCount={unseenOpenTicketIds.length}
          alertHint="New client ticket"
          onClick={handleOpenTicketsClick}
        />
        <StatCard
          compact
          label="In progress"
          value={inProgressCount}
          icon={Clock}
          accent="bg-amber-50 text-amber-600"
          isActive={statusFilter === 'In Progress'}
          onClick={() => toggleStatusFilter('In Progress')}
        />
        <StatCard
          compact
          label="Resolved"
          value={resolvedCount}
          icon={CheckCircle2}
          accent="bg-emerald-50 text-emerald-600"
          isActive={statusFilter === 'Resolved'}
          hasAlert={hasResolvedAlert}
          alertCount={unseenResolvedTicketIds.length}
          alertHint="Client confirmed"
          onClick={handleResolvedClick}
        />
        <StatCard
          compact
          label="Report"
          value={tickets.length}
          icon={FileText}
          accent="bg-violet-50 text-violet-600"
          isActive={statusFilter === 'Report'}
          onClick={() => toggleStatusFilter('Report')}
        />
      </div>

      <div className="csk-admin-metrics-grid csk-admin-metrics-grid--secondary">
        <StatCard
          compact
          label="Technical team"
          value={TECHNICAL_TEAM.length}
          icon={Users}
          accent="bg-slate-100 text-slate-600"
          isActive={statusFilter === 'Technical Team'}
          onClick={() => toggleStatusFilter('Technical Team')}
        />
        <StatCard compact label="Awaiting client" value={awaitingClientCount} icon={User} accent="bg-amber-50 text-amber-600" />
        <StatCard compact label="On-time closures" value={slaSplit.onTime.length} icon={CheckCircle2} accent="bg-emerald-50 text-emerald-600" />
        <StatCard compact label="Delayed closures" value={slaSplit.delayed.length} icon={Clock} accent="bg-rose-50 text-rose-600" />
      </div>

      {showResolvedSplit ? (
        <div className="csk-admin-dashboard-split">
          <CompletionSplitBoard
            layout="dashboard"
            pairs={resolvedCompletionPairs}
            onTimeTitle="On time completion"
            delayedTitle="Delayed work orders"
            onTimeHint="Closed within SLA"
            delayedHint="Closed after SLA deadline"
            onCardClick={(workOrder, ticket) => {
              if (ticket) handleTicketClick(ticket);
            }}
          />
        </div>
      ) : showTeamView ? (
        <AdminTechnicalTeamPanel team={TECHNICAL_TEAM} workOrders={workOrders} />
      ) : showReportView ? (
        <AdminOperationsReportPanel
          tickets={tickets}
          workOrders={workOrders}
          openCount={openCount}
          inProgressCount={inProgressCount}
          resolvedCount={resolvedCount}
          awaitingClientCount={awaitingClientCount}
          activeWo={activeWo}
          slaSplit={slaSplit}
          teamAvailableCount={teamAvailableCount}
          onExportExcel={handleExportExcel}
        />
      ) : (
          <section className="csk-admin-widget csk-admin-widget--main">
            <div className="csk-admin-widget-head">
              <div>
                <h2 className="text-base font-bold text-slate-900">
                  {showWoCentricView ? `${statusFilter} queue` : 'Operations queue'}
                </h2>
                <p className="mt-0.5 text-sm text-slate-500">
                  {statusFilter === 'In Progress'
                    ? 'Tickets & work orders in the field'
                    : statusFilter === 'Open'
                      ? 'New tickets awaiting review'
                      : 'Select a row to assign engineers and issue work orders'}
                </p>
              </div>
              <span className="csk-admin-widget-link">{filteredTickets.length} items</span>
            </div>
            <div className="csk-admin-table-wrap overflow-x-auto">
              <table className="csk-admin-table min-w-full text-left text-sm">
                <thead>
                  <tr>
                    <th>TT / WO</th>
                    <th>Client</th>
                    <th>Issue</th>
                    <th>Priority / Timeline</th>
                    <th>Status</th>
                    <th>Engineer</th>
                  </tr>
                </thead>
                <tbody>
                {filteredTickets.map((t) => {
                  const isNewOpen = unseenOpenTicketIds.includes(t.id);
                  const isNewResolved = unseenResolvedTicketIds.includes(t.id);
                  const isSelected = selectedTicketId === t.id;
                  const statusLabel = t.workOrderId
                    ? adminTicketStatusLabel(t)
                    : isPendingClientConfirm(t)
                      ? 'Awaiting client'
                      : t.status;
                  const statusStyles = {
                    ...STATUS_STYLES,
                    'Awaiting client': 'bg-amber-100 text-amber-800',
                    'WO · In progress': 'bg-amber-100 text-amber-800',
                    'WO · Awaiting client': 'bg-amber-100 text-amber-800',
                    'WO · Resolved': 'bg-emerald-100 text-emerald-800',
                    'Work order': 'bg-teal-100 text-teal-800',
                  };
                  return (
                  <tr
                    key={t.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleTicketClick(t)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleTicketClick(t);
                      }
                    }}
                    className={`csk-admin-table-row cursor-pointer ${
                      isSelected
                        ? 'is-selected'
                        : isNewOpen
                          ? 'is-new-open'
                          : isNewResolved
                            ? 'is-new-resolved'
                            : ''
                    }`}
                  >
                    <td className="font-medium text-slate-900">
                      <span className="inline-flex flex-col gap-0.5">
                        <span className="inline-flex items-center gap-2">
                          {ticketDisplayRef(t)}
                          {isNewOpen ? (
                            <span className="rounded-full bg-rose-600 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
                              New
                            </span>
                          ) : null}
                          {isNewResolved ? (
                            <span className="rounded-full bg-emerald-600 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
                              Confirmed
                            </span>
                          ) : null}
                          <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                            ticketTypeMeta(t)?.code === 'WO'
                              ? 'bg-teal-100 text-teal-800'
                              : 'bg-violet-100 text-violet-800'
                          }`}>
                            {ticketTypeMeta(t)?.code || 'TT'}
                          </span>
                          {t.workOrderId && t.id !== t.workOrderId ? (
                            <span className="rounded-full bg-teal-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-teal-800">
                              Linked WO
                            </span>
                          ) : null}
                        </span>
                        {t.workOrderId && t.id !== t.workOrderId ? (
                          <span className="text-xs font-normal text-slate-500">{t.id} → {t.workOrderId}</span>
                        ) : formatTicketCoordinates(t) ? (
                          <TicketDirectionsLink ticket={t} className="text-xs font-normal" />
                        ) : null}
                      </span>
                    </td>
                    <td className="text-slate-700">{t.client}</td>
                    <td className="max-w-xs text-slate-600">{t.issue}</td>
                    <td><TimelineBadge timeline={t.priority} /></td>
                    <td>
                      <Badge label={statusLabel} map={statusStyles} />
                    </td>
                    <td className="text-slate-600">
                      {t.engineer || '—'}
                      {t.clientConfirmed && t.serviceRating ? (
                        <span className="ml-2 inline-flex items-center gap-0.5 text-amber-500">
                          {renderStars(t.serviceRating, 'h-3 w-3')}
                        </span>
                      ) : null}
                    </td>
                  </tr>
                );
                })}
              </tbody>
            </table>
            {filteredTickets.length === 0 ? (
              <p className="csk-admin-empty px-4 py-10 text-center text-sm text-slate-500">
                {statusFilter
                  ? `No ${statusFilter.toLowerCase()} tickets. Click the card again to show all.`
                  : 'No trouble tickets yet.'}
              </p>
            ) : null}
            </div>
          </section>
      )}

      {!showReportView ? (
      <AdminDashboardBarChart
        title={
          showResolvedSplit
            ? 'SLA completion statistics'
            : showTeamView
              ? 'Team availability'
              : 'Ticket statistics'
        }
        subtitle={
          showResolvedSplit
            ? 'Client-confirmed closures by SLA outcome'
            : showTeamView
              ? 'Field team capacity and rank distribution'
              : 'Current ticket distribution across queues'
        }
        items={
          showResolvedSplit
            ? [
                { label: 'On time', value: slaSplit.onTime.length, highlight: true },
                { label: 'Delayed', value: slaSplit.delayed.length },
                { label: 'Critical', value: resolvedCompletionPairs.filter(({ ticket }) => ticket?.priority === 'Critical').length },
                { label: 'Major', value: resolvedCompletionPairs.filter(({ ticket }) => ticket?.priority === 'Major').length },
                { label: 'Minor', value: resolvedCompletionPairs.filter(({ ticket }) => ticket?.priority === 'Minor').length },
                { label: '24 HR', value: resolvedCompletionPairs.filter(({ ticket }) => normalizeTimelineValue(ticket?.priority) === '24 HR').length },
                { label: '7 Days', value: resolvedCompletionPairs.filter(({ ticket }) => normalizeTimelineValue(ticket?.priority) === '7 Days').length },
                { label: '2 Weeks', value: resolvedCompletionPairs.filter(({ ticket }) => normalizeTimelineValue(ticket?.priority) === '2 Weeks').length },
              ]
            : showTeamView
              ? [
                  { label: 'Available', value: teamAvailableCount, highlight: true },
                  { label: 'Busy', value: TECHNICAL_TEAM.length - teamAvailableCount },
                  { label: 'Leaders', value: teamRankCounts.leaders },
                  { label: 'Engineers', value: teamRankCounts.engineers },
                  { label: 'Technicians', value: teamRankCounts.technicians },
                ]
              : [
                  { label: 'Open', value: openCount, highlight: true },
                  { label: 'In progress', value: inProgressCount },
                  { label: 'Awaiting', value: awaitingClientCount },
                  { label: 'Resolved', value: resolvedCount },
                  { label: 'Active WO', value: activeWo },
                ]
        }
      />
      ) : null}

      {selectedTicket ? (
        <AdminTicketReviewSidebar
          ticket={selectedTicket}
          workOrders={workOrders}
          onClose={() => setSelectedTicketId(null)}
          onIssueWorkOrder={onIssueWorkOrder}
        />
      ) : null}
    </div>
  );
}

function EngineerWorkOrderSidebar({
  workOrder,
  ticket,
  isNew,
  onClose,
  onUpdateStatus,
  onConfirmWork,
  onCompleteWork,
}) {
  const materialPlan = getWorkOrderMaterialPlan(workOrder);
  const [selectedIds, setSelectedIds] = useState(() =>
    materialPlan.map((m) => m.id).filter((id) => FIELD_TOOL_BY_ID[id])
  );
  const [plannedQty, setPlannedQty] = useState(() => {
    const map = {};
    materialPlan.forEach((m) => {
      map[m.id] = m.plannedQty || 1;
    });
    return map;
  });
  const [usedQty, setUsedQty] = useState(() => {
    const map = {};
    (workOrder.reconciledMaterials || materialPlan).forEach((m) => {
      map[m.id] = m.usedQty ?? m.plannedQty ?? 1;
    });
    return map;
  });
  const [sitePhotos, setSitePhotos] = useState(() => workOrder.sitePhotos || []);
  const [toolError, setToolError] = useState('');
  const [completeError, setCompleteError] = useState('');
  const [photoBusy, setPhotoBusy] = useState(false);

  useEffect(() => {
    const plan = getWorkOrderMaterialPlan(workOrder);
    setSelectedIds(plan.map((m) => m.id).filter((id) => FIELD_TOOL_BY_ID[id]));
    const planned = {};
    const used = {};
    plan.forEach((m) => {
      planned[m.id] = m.plannedQty || 1;
      used[m.id] = m.usedQty ?? m.plannedQty ?? 1;
    });
    setPlannedQty(planned);
    setUsedQty(used);
    setSitePhotos(workOrder.sitePhotos || []);
    setToolError('');
    setCompleteError('');
  }, [workOrder]);

  const toggleItem = (toolId) => {
    setSelectedIds((current) => {
      if (current.includes(toolId)) return current.filter((id) => id !== toolId);
      setPlannedQty((q) => ({ ...q, [toolId]: q[toolId] || 1 }));
      return [...current, toolId];
    });
    setToolError('');
  };

  const setPlannedQuantity = (toolId, raw) => {
    const n = Math.max(1, parseInt(String(raw), 10) || 1);
    setPlannedQty((q) => ({ ...q, [toolId]: n }));
  };

  const setUsedQuantity = (toolId, raw) => {
    const n = Math.max(0, parseInt(String(raw), 10) || 0);
    setUsedQty((q) => ({ ...q, [toolId]: n }));
    setCompleteError('');
  };

  const buildMaterialPlanPayload = () =>
    selectedIds.map((id) => {
      const meta = FIELD_TOOL_BY_ID[id];
      return {
        id,
        label: meta?.label || id,
        kind: meta?.kind || 'tool',
        plannedQty: plannedQty[id] || 1,
      };
    });

  const handleConfirmWork = () => {
    if (selectedIds.length === 0) {
      setToolError('Select at least one tool or material.');
      return;
    }
    const plan = buildMaterialPlanPayload();
    onConfirmWork?.({
      workOrderId: workOrder.id,
      materialPlan: plan,
      selectedTools: plan.map((p) => formatMaterialQtyLine(p)),
    });
  };

  const handlePhotoPick = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    setPhotoBusy(true);
    try {
      const remaining = Math.max(0, 2 - sitePhotos.length);
      const added = await readImageFilesAsDataUrls(files, remaining);
      setSitePhotos((prev) => [...prev, ...added].slice(0, 2));
      setCompleteError('');
    } finally {
      setPhotoBusy(false);
      e.target.value = '';
    }
  };

  const removePhoto = (index) => {
    setSitePhotos((prev) => prev.filter((_, i) => i !== index));
  };

  const handleCompleteJob = () => {
    const plan = getWorkOrderMaterialPlan(workOrder);
    const reconciledMaterials = plan.map((item) => ({
      ...item,
      usedQty: usedQty[item.id] ?? item.plannedQty ?? 0,
    }));
    const invalid = reconciledMaterials.find(
      (item) => item.usedQty === '' || item.usedQty == null || Number.isNaN(Number(item.usedQty))
    );
    if (invalid) {
      setCompleteError('Enter the quantity used for each item.');
      return;
    }
    onCompleteWork?.({
      workOrderId: workOrder.id,
      reconciledMaterials,
      sitePhotos,
    });
  };

  const needsToolConfirmation = workOrder.status === 'Assigned' && !workOrder.workConfirmed;
  const canReconcile = workOrder.status === 'In Progress';
  const confirmedPlan = workOrder.workConfirmed ? getWorkOrderMaterialPlan(workOrder) : [];

  useCloseOnEscape(onClose);

  return (
    <div className="csk-engineer-sidebar-root fixed inset-0 z-50 flex justify-end" role="presentation">
      <button
        type="button"
        className="csk-engineer-sidebar-backdrop absolute inset-0 bg-slate-900/45 backdrop-blur-[2px]"
        onClick={onClose}
        aria-label="Close work order panel"
      />
      <aside
        className="csk-engineer-sidebar relative z-10 flex h-full w-full max-w-md flex-col border-l border-slate-200/80 bg-white shadow-2xl"
        role="dialog"
        aria-labelledby="engineer-wo-review-title"
        aria-modal="true"
      >
        <div className="flex items-start justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-teal-600">Work order review</p>
            <h2 id="engineer-wo-review-title" className="mt-1 text-lg font-semibold text-slate-900">
              {workOrder.id}
            </h2>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Badge label={workOrder.status} map={STATUS_STYLES} />
              {isNew ? (
                <span className="rounded-full bg-teal-600 px-2 py-0.5 text-[10px] font-bold uppercase text-white">
                  New
                </span>
              ) : null}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <TicketPdfDownloadButton
              ticket={ticket}
              workOrder={workOrder}
              reportTitle="Work order report"
              compact
            />
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          <section>
            <h3 className="text-sm font-semibold text-slate-900">Task</h3>
            <p className="mt-2 text-sm text-slate-700">{workOrder.title}</p>
            <dl className="mt-3 space-y-2 text-sm text-slate-600">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 shrink-0 text-slate-400" />
                <span>Scheduled {workOrder.scheduled}</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 shrink-0 text-slate-400" />
                <span>{workOrder.zone}</span>
              </div>
              <div className="flex items-center gap-2">
                <Zap className="h-4 w-4 shrink-0 text-slate-400" />
                <span>Linked ticket {workOrder.ticketId}</span>
              </div>
            </dl>
            <WorkOrderProgressStepper workOrder={workOrder} />
          </section>

          {ticket ? (
            <section className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4">
              <h3 className="text-sm font-semibold text-slate-900">Client & location</h3>
              <dl className="mt-3 space-y-3 text-sm">
                <div className="flex items-start gap-3">
                  <User className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                  <div>
                    <dt className="text-slate-500">Client</dt>
                    <dd className="font-medium text-slate-900">{ticket.client}</dd>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Phone className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                  <div>
                    <dt className="text-slate-500">Contact</dt>
                    <dd className="font-medium text-slate-900">{ticket.contact}</dd>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                  <div>
                    <dt className="text-slate-500">Site location</dt>
                    <dd className="font-medium">
                      {ticket.location && getTicketDirectionsUrl(ticket) ? (
                        <TicketDirectionsLink ticket={ticket} label={ticket.location} />
                      ) : (
                        <span className="text-slate-900">{ticket.location || '—'}</span>
                      )}
                    </dd>
                    {formatTicketCoordinates(ticket) ? (
                      <dd className="mt-1 font-mono text-xs">
                        <TicketDirectionsLink ticket={ticket} className="text-xs" />
                      </dd>
                    ) : null}
                  </div>
                </div>
              </dl>
              <div className="mt-4 border-t border-slate-200 pt-3">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Reported issue</p>
                <p className="mt-1 text-sm text-slate-700">{ticket.issue}</p>
                <div className="mt-2">
                  <TimelineBadge timeline={ticket.priority} />
                </div>
              </div>
            </section>
          ) : null}

          {ticket?.updates?.length ? (
            <section className="mt-4">
              <h3 className="text-sm font-semibold text-slate-900">Ticket activity</h3>
              <ol className="mt-3 space-y-3">
                {ticket.updates.map((u, i) => (
                  <li key={`${ticket.id}-eng-u-${i}`} className="flex gap-3 text-sm">
                    <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-teal-400" />
                    <div>
                      <p className="text-slate-800">{u.message}</p>
                      <p className="text-xs text-slate-500">
                        {formatTimestamp(u.at)} · {u.by}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            </section>
          ) : null}

          {needsToolConfirmation ? (
            <section className="csk-engineer-tools mt-6 rounded-xl border border-amber-200 bg-amber-50/50 p-4">
              <div className="flex items-center gap-2">
                <Wrench className="h-4 w-4 text-amber-700" />
                <h3 className="text-sm font-semibold text-slate-900">Tools & equipment</h3>
              </div>
              <p className="mt-1 text-sm text-slate-600">
                Select tools and materials, enter planned quantities, then confirm work.
              </p>
              <ul className="csk-engineer-tools-list mt-4 space-y-2">
                {FIELD_TOOLS.map((tool) => {
                  const checked = selectedIds.includes(tool.id);
                  return (
                    <li key={tool.id}>
                      <div
                        className={`rounded-lg border transition ${
                          checked
                            ? 'border-teal-400 bg-teal-50 ring-1 ring-teal-200'
                            : 'border-slate-200 bg-white hover:border-teal-200'
                        }`}
                      >
                        <label className="flex cursor-pointer items-center gap-3 px-3 py-2.5">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleItem(tool.id)}
                            className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                          />
                          <span className="min-w-0 flex-1 text-sm font-medium text-slate-800">{tool.label}</span>
                          {tool.kind === 'material' ? (
                            <span className="rounded bg-violet-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-violet-700">
                              Material
                            </span>
                          ) : null}
                        </label>
                        {checked ? (
                          <div className="flex items-center gap-2 border-t border-teal-100 px-3 py-2">
                            <label className="text-xs text-slate-500" htmlFor={`plan-qty-${tool.id}`}>
                              Qty needed
                            </label>
                            <input
                              id={`plan-qty-${tool.id}`}
                              type="number"
                              min={1}
                              value={plannedQty[tool.id] || 1}
                              onChange={(e) => setPlannedQuantity(tool.id, e.target.value)}
                              className="w-20 rounded border border-slate-200 px-2 py-1 text-sm outline-none focus:border-teal-500"
                            />
                          </div>
                        ) : null}
                      </div>
                    </li>
                  );
                })}
              </ul>
              {toolError ? <p className="mt-3 text-sm text-rose-600">{toolError}</p> : null}
              <button
                type="button"
                onClick={handleConfirmWork}
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-amber-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-amber-700"
              >
                <Check className="h-4 w-4" />
                Confirm work
              </button>
            </section>
          ) : null}

          {workOrder.workConfirmed && confirmedPlan.length ? (
            <section className="mt-6 rounded-xl border border-teal-200 bg-teal-50/40 p-4">
              <h3 className="text-sm font-semibold text-teal-900">Planned tools & materials</h3>
              <ul className="mt-2 space-y-1.5 text-sm text-teal-900">
                {confirmedPlan.map((item) => (
                  <li key={item.id} className="flex justify-between gap-2">
                    <span>{item.label}</span>
                    <span className="font-semibold">× {item.plannedQty || 1}</span>
                  </li>
                ))}
              </ul>
              {workOrder.workConfirmedAt ? (
                <p className="mt-2 text-xs text-teal-700">
                  Confirmed {formatTimestamp(workOrder.workConfirmedAt)}
                </p>
              ) : null}
            </section>
          ) : null}

          {canReconcile ? (
            <section className="mt-6 rounded-xl border border-violet-200 bg-violet-50/40 p-4">
              <h3 className="text-sm font-semibold text-slate-900">Reconcile materials used</h3>
              <p className="mt-1 text-sm text-slate-600">
                After field work, record what was actually used before completing the job.
              </p>
              <ul className="mt-4 space-y-3">
                {confirmedPlan.map((item) => (
                  <li key={`used-${item.id}`} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-violet-100 bg-white px-3 py-2">
                    <div>
                      <p className="text-sm font-medium text-slate-800">{item.label}</p>
                      <p className="text-xs text-slate-500">Planned: {item.plannedQty || 1}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="text-xs text-slate-500" htmlFor={`used-qty-${item.id}`}>
                        Used
                      </label>
                      <input
                        id={`used-qty-${item.id}`}
                        type="number"
                        min={0}
                        value={usedQty[item.id] ?? item.plannedQty ?? 0}
                        onChange={(e) => setUsedQuantity(item.id, e.target.value)}
                        className="w-20 rounded border border-slate-200 px-2 py-1 text-sm outline-none focus:border-violet-500"
                      />
                    </div>
                  </li>
                ))}
              </ul>

              <div className="mt-5 border-t border-violet-100 pt-4">
                <h4 className="text-sm font-semibold text-slate-900">Site photos (optional)</h4>
                <p className="mt-1 text-xs text-slate-500">Upload 1 or 2 photos as proof of completed work.</p>
                {sitePhotos.length > 0 ? (
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    {sitePhotos.map((photo, index) => (
                      <div key={`${photo.name}-${index}`} className="relative overflow-hidden rounded-lg border border-slate-200">
                        <img src={photo.dataUrl} alt={`Site ${index + 1}`} className="h-24 w-full object-cover" />
                        <button
                          type="button"
                          onClick={() => removePhoto(index)}
                          className="absolute right-1 top-1 rounded bg-slate-900/70 p-1 text-white"
                          aria-label="Remove photo"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : null}
                {sitePhotos.length < 2 ? (
                  <label className="mt-3 inline-flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-violet-300 bg-white px-3 py-2 text-sm font-medium text-violet-700 hover:bg-violet-50">
                    <ImagePlus className="h-4 w-4" />
                    {photoBusy ? 'Uploading…' : 'Add site photo'}
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      className="sr-only"
                      onChange={handlePhotoPick}
                      disabled={photoBusy}
                    />
                  </label>
                ) : null}
              </div>

              {completeError ? <p className="mt-3 text-sm text-rose-600">{completeError}</p> : null}
              <button
                type="button"
                onClick={handleCompleteJob}
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700"
              >
                <CheckCircle2 className="h-4 w-4" />
                Reconcile & mark complete
              </button>
            </section>
          ) : null}

          {workOrder.status !== 'Closed' && !canReconcile ? (
            <section className="mt-6 rounded-xl border border-teal-200 bg-teal-50/50 p-4">
              <h3 className="text-sm font-semibold text-slate-900">Field actions</h3>
              <p className="mt-1 text-sm text-slate-600">
                {needsToolConfirmation
                  ? 'Confirm tools and materials above before starting field work.'
                  : 'Start work on site when ready.'}
              </p>
              <div className="mt-4 flex flex-col gap-2">
                {workOrder.status === 'Assigned' && workOrder.workConfirmed ? (
                  <button
                    type="button"
                    onClick={() => onUpdateStatus({ workOrderId: workOrder.id, status: 'In Progress' })}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-teal-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-teal-700"
                  >
                    <Wrench className="h-4 w-4" />
                    Start work
                  </button>
                ) : null}
              </div>
            </section>
          ) : null}

          {workOrder.status === 'Closed' ? (
            <div className="mt-6 space-y-4">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
                This work order is completed.
              </div>
              {workOrder.reconciledMaterials?.length ? (
                <section className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <h3 className="text-sm font-semibold text-slate-900">Materials reconciled</h3>
                  <ul className="mt-2 space-y-1 text-sm text-slate-700">
                    {workOrder.reconciledMaterials.map((item) => (
                      <li key={`rec-${item.id}`} className="flex justify-between gap-2">
                        <span>{item.label}</span>
                        <span>
                          {item.usedQty} used <span className="text-slate-400">/ {item.plannedQty} planned</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                </section>
              ) : null}
              {workOrder.sitePhotos?.length ? (
                <section className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <h3 className="text-sm font-semibold text-slate-900">Site photos</h3>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    {workOrder.sitePhotos.map((photo, index) => (
                      <img
                        key={`${photo.name}-${index}`}
                        src={photo.dataUrl}
                        alt={`Site proof ${index + 1}`}
                        className="h-24 w-full rounded-lg border border-slate-200 object-cover"
                      />
                    ))}
                  </div>
                </section>
              ) : null}
            </div>
          ) : null}
        </div>
      </aside>
    </div>
  );
}

function EngineerView({
  workOrders,
  tickets,
  notifications,
  engineerName = ENGINEER_DEMO_NAME,
  unseenWorkOrderIds = [],
  onAcknowledgeWorkOrders,
  onMarkNotificationRead,
  onMarkAllNotificationsRead,
  onUpdateWorkOrder,
  onSubmitDelayReason,
  onConfirmWork,
  onCompleteWork,
}) {
  const engineerProfile = ENGINEERS.find((e) => e.name === engineerName) || {
    name: engineerName,
    role: 'Engineer',
    zone: 'Kenya',
  };

  const [statusFilter, setStatusFilter] = useState(null);
  const [selectedWoId, setSelectedWoId] = useState(null);
  const [showNotifications, setShowNotifications] = useState(false);
  const [query, setQuery] = useState('');

  const allMine = useMemo(
    () => workOrders.filter((w) => w.engineer === engineerName),
    [workOrders, engineerName]
  );

  const assignedCount = allMine.filter((w) => w.status === 'Assigned').length;
  const inProgressCount = allMine.filter((w) => w.status === 'In Progress').length;
  const closedCount = allMine.filter((w) => w.status === 'Closed').length;

  const unseenAssignedIds = useMemo(
    () =>
      unseenWorkOrderIds.filter((id) =>
        allMine.some((w) => w.id === id && w.status === 'Assigned')
      ),
    [unseenWorkOrderIds, allMine]
  );
  const hasAssignedAlert = unseenAssignedIds.length > 0;

  const engineerNotifications = useMemo(
    () =>
      notifications
        .filter((n) => n.engineer === engineerName)
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
    [notifications, engineerName]
  );
  const unreadCount = engineerNotifications.filter((n) => !n.read).length;

  const filteredWorkOrders = useMemo(() => {
    let list = allMine;
    if (statusFilter) {
      list = list.filter((w) => w.status === statusFilter);
    }
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (w) =>
        w.id.toLowerCase().includes(q) ||
        w.title.toLowerCase().includes(q) ||
        w.ticketId.toLowerCase().includes(q)
    );
  }, [allMine, statusFilter, query]);

  const completedPairs = useMemo(() => {
    let list = allMine.filter((w) => w.status === 'Closed' && w.completedAt);
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (w) =>
          w.id.toLowerCase().includes(q) ||
          w.title.toLowerCase().includes(q) ||
          w.ticketId.toLowerCase().includes(q)
      );
    }
    return list
      .map((w) => ({ workOrder: w, ticket: tickets.find((t) => t.id === w.ticketId) }))
      .filter(({ ticket }) => ticket);
  }, [allMine, tickets, query]);

  const showCompletedSplit = statusFilter === 'Closed';

  const selectedWorkOrder = useMemo(
    () => allMine.find((w) => w.id === selectedWoId) || null,
    [allMine, selectedWoId]
  );

  const selectedTicket = useMemo(
    () => (selectedWorkOrder ? tickets.find((t) => t.id === selectedWorkOrder.ticketId) : null),
    [tickets, selectedWorkOrder]
  );

  const toggleStatusFilter = (status) => {
    setStatusFilter((current) => (current === status ? null : status));
    setSelectedWoId(null);
  };

  const handleAssignedClick = () => {
    toggleStatusFilter('Assigned');
    if (hasAssignedAlert) {
      onAcknowledgeWorkOrders?.(unseenAssignedIds);
    }
  };

  const handleWorkOrderClick = (wo) => {
    setSelectedWoId((current) => {
      if (current === wo.id) return null;
      if (unseenWorkOrderIds.includes(wo.id)) {
        onAcknowledgeWorkOrders?.([wo.id]);
      }
      return wo.id;
    });
  };

  return (
    <div className="space-y-6">
      <RoleViewBanner role="Engineer" />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
          <p className="text-sm font-medium text-slate-500">{engineerProfile.name}</p>
          <p className="text-xs text-slate-400">
            {engineerProfile.role} · {engineerProfile.zone} zone
          </p>
        </div>
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowNotifications((v) => !v)}
            className="relative inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50"
            aria-expanded={showNotifications}
            aria-haspopup="true"
          >
            <Bell className="h-4 w-4" />
            Notifications
            {unreadCount > 0 ? (
              <span className="flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-teal-500 px-1.5 text-xs font-semibold text-white">
                {unreadCount}
              </span>
            ) : null}
          </button>
          {showNotifications ? (
            <NotificationPanel
              notifications={engineerNotifications}
              onMarkRead={onMarkNotificationRead}
              onMarkAllRead={onMarkAllNotificationsRead}
              onClose={() => setShowNotifications(false)}
            />
          ) : null}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard
          label="Assigned"
          value={assignedCount}
          icon={AlertCircle}
          accent="bg-teal-50 text-teal-600"
          isActive={statusFilter === 'Assigned'}
          hasAlert={hasAssignedAlert}
          alertCount={unseenAssignedIds.length}
          alertHint="New work order — click to review"
          onClick={handleAssignedClick}
        />
        <StatCard
          label="In progress"
          value={inProgressCount}
          icon={Clock}
          accent="bg-amber-50 text-amber-600"
          isActive={statusFilter === 'In Progress'}
          onClick={() => toggleStatusFilter('In Progress')}
        />
        <StatCard
          label="Completed"
          value={closedCount}
          icon={CheckCircle2}
          accent="bg-emerald-50 text-emerald-600"
          isActive={statusFilter === 'Closed'}
          onClick={() => toggleStatusFilter('Closed')}
        />
      </div>

      <div className="relative max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search work orders…"
          className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm shadow-sm outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20"
        />
      </div>

      <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 px-4 py-3">
          <h2 className="text-base font-semibold text-slate-900">
            {showCompletedSplit ? 'Completed work orders' : 'My work orders'}
          </h2>
          <p className="text-sm text-slate-500">
            {showCompletedSplit
              ? 'Grouped by SLA — submit a delay reason for any work order in the delayed column'
              : statusFilter
                ? `Showing ${statusFilter.toLowerCase()} work orders only — click the card again to show all`
                : 'Click a work order to view client details, location, and update job status'}
          </p>
        </div>
        {showCompletedSplit ? (
          <div className="p-4">
            <CompletionSplitBoard
              pairs={completedPairs}
              engineerMode
              onSubmitDelayReason={onSubmitDelayReason}
              onTimeTitle="On time completion"
              delayedTitle="Delayed work orders"
              onTimeHint="You closed within the SLA window"
              delayedHint="Closed after SLA — reason required"
              onCardClick={(workOrder) => handleWorkOrderClick(workOrder)}
            />
          </div>
        ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="px-4 py-2 font-medium">Work order</th>
                <th className="px-4 py-2 font-medium">TT / WO</th>
                <th className="px-4 py-2 font-medium">Task</th>
                <th className="px-4 py-2 font-medium">Zone</th>
                <th className="px-4 py-2 font-medium">Scheduled</th>
                <th className="px-4 py-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredWorkOrders.map((w) => {
                const isNew = unseenWorkOrderIds.includes(w.id);
                const isSelected = selectedWoId === w.id;
                return (
                  <tr
                    key={w.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleWorkOrderClick(w)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleWorkOrderClick(w);
                      }
                    }}
                    className={`cursor-pointer ${
                      isSelected
                        ? 'bg-teal-50 ring-1 ring-inset ring-teal-200'
                        : isNew
                          ? 'bg-teal-50/80 hover:bg-teal-50'
                          : 'hover:bg-slate-50/80'
                    }`}
                  >
                    <td className="px-4 py-3 font-medium text-slate-900">
                      <span className="inline-flex items-center gap-2">
                        {w.id}
                        {isNew ? (
                          <span className="rounded-full bg-teal-600 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
                            New
                          </span>
                        ) : null}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{w.ticketId}</td>
                    <td className="max-w-xs px-4 py-3 text-slate-700">{w.title}</td>
                    <td className="px-4 py-3 text-slate-600">{w.zone}</td>
                    <td className="px-4 py-3 text-slate-600">{w.scheduled}</td>
                    <td className="px-4 py-3">
                      <Badge label={w.status} map={STATUS_STYLES} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filteredWorkOrders.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-slate-500">
              {statusFilter
                ? `No ${statusFilter.toLowerCase()} work orders. Click the card again to show all.`
                : 'No work orders on your queue yet.'}
            </p>
          ) : null}
        </div>
        )}
      </section>

      {selectedWorkOrder ? (
        <EngineerWorkOrderSidebar
          workOrder={selectedWorkOrder}
          ticket={selectedTicket}
          isNew={unseenWorkOrderIds.includes(selectedWorkOrder.id)}
          onClose={() => setSelectedWoId(null)}
          onUpdateStatus={onUpdateWorkOrder}
          onConfirmWork={onConfirmWork}
          onCompleteWork={(payload) => {
            onCompleteWork?.(payload);
            setSelectedWoId(null);
          }}
        />
      ) : null}
    </div>
  );
}

export default function CustomerServicesKenya() {
  const [role, setRole] = useState('Client');
  const [tickets, setTickets] = useState(SEED_TICKETS);
  const [notifications, setNotifications] = useState(SEED_NOTIFICATIONS);
  const [adminUnseenOpenIds, setAdminUnseenOpenIds] = useState(['WO-2410']);
  const [adminUnseenResolvedIds, setAdminUnseenResolvedIds] = useState([]);
  const [workOrders, setWorkOrders] = useState(SEED_WORK_ORDERS);
  const [engineerUnseenWoIds, setEngineerUnseenWoIds] = useState(['WO-2405']);
  const prevRoleRef = useRef(role);

  const unseenOpenTicketIds = useMemo(
    () => adminUnseenOpenIds.filter((id) => tickets.some((t) => t.id === id && t.status === 'Open')),
    [adminUnseenOpenIds, tickets]
  );

  const unseenResolvedTicketIds = useMemo(
    () =>
      adminUnseenResolvedIds.filter((id) =>
        tickets.some((t) => t.id === id && isAdminResolved(t))
      ),
    [adminUnseenResolvedIds, tickets]
  );

  const engineerUnseenWorkOrders = useMemo(
    () =>
      workOrders.filter(
        (w) =>
          engineerUnseenWoIds.includes(w.id) &&
          w.engineer === ENGINEER_DEMO_NAME &&
          w.status !== 'Closed'
      ),
    [workOrders, engineerUnseenWoIds]
  );

  useEffect(() => {
    if (role === 'Admin' && prevRoleRef.current !== 'Admin') {
      if (unseenOpenTicketIds.length > 0 || unseenResolvedTicketIds.length > 0) {
        playAdminBeep();
      }
    }
    if (role === 'Engineer' && prevRoleRef.current !== 'Engineer' && engineerUnseenWorkOrders.length > 0) {
      playAdminBeep();
    }
    prevRoleRef.current = role;
  }, [role, unseenOpenTicketIds.length, unseenResolvedTicketIds.length, engineerUnseenWorkOrders.length]);

  const handleCreateTicket = ({ ticketType, typeCode, issue, priority, coordinates }) => {
    const id = nextTicketId(tickets, typeCode);
    const now = new Date().toISOString();
    const coords = buildCoordinatesFromInput(coordinates);
    const coordsLabel = coords?.coordinatesLabel || coordinates;
    const typeLabel = TICKET_TYPES.find((t) => t.code === typeCode)?.label || ticketType;
    const ticket = {
      id,
      ticketType,
      typeCode,
      client: CLIENT_ACCOUNT.name,
      contact: CLIENT_ACCOUNT.contact,
      location: CLIENT_ACCOUNT.address,
      ...coords,
      issue,
      category: ticketType,
      priority,
      status: 'Open',
      created: todayIso(),
      engineer: '',
      workOrderId: '',
      updates: [
        { at: now, message: `${typeLabel} opened by client`, by: 'Client' },
        { at: now, message: `Site coordinates: ${coordsLabel}`, by: 'Client' },
        { at: now, message: 'Ticket received — awaiting admin review', by: 'System' },
      ],
    };

    setTickets((prev) => [ticket, ...prev]);
    setAdminUnseenOpenIds((prev) => [id, ...prev]);
    playAdminBeep();

    const notifId = `N-${Date.now()}`;
    setNotifications((prev) => [
      {
        id: notifId,
        client: CLIENT_ACCOUNT.name,
        ticketId: id,
        title: 'Ticket opened',
        message: `Your ${typeCode} ticket ${id} has been logged. We will update you shortly.`,
        createdAt: now,
        read: false,
      },
      ...prev,
    ]);
  };

  const handleUpdateTicketCoordinates = (ticketId, coordinatesInput) => {
    const coords = buildCoordinatesFromInput(coordinatesInput);
    if (!coords) return;

    const now = new Date().toISOString();
    const existing = tickets.find((t) => t.id === ticketId);
    const linkedWo = existing ? getLinkedWorkOrder(existing, workOrders) : null;

    setTickets((prev) =>
      prev.map((t) =>
        t.id === ticketId
          ? {
              ...t,
              ...coords,
              updates: [
                ...(t.updates || []),
                {
                  at: now,
                  message: `Client updated site coordinates: ${coords.coordinatesLabel}`,
                  by: 'Client',
                },
              ],
            }
          : t
      )
    );

    if (existing?.status === 'Open') {
      setAdminUnseenOpenIds((prev) => (prev.includes(ticketId) ? prev : [ticketId, ...prev]));
    }

    if (linkedWo?.id) {
      setEngineerUnseenWoIds((prev) => (prev.includes(linkedWo.id) ? prev : [linkedWo.id, ...prev]));
    }
  };

  const handleMarkNotificationRead = (id) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
  };

  const handleMarkAllNotificationsRead = () => {
    setNotifications((prev) =>
      prev.map((n) =>
        n.client === CLIENT_ACCOUNT.name ? { ...n, read: true } : n
      )
    );
  };

  const handleAcknowledgeOpenAlerts = () => {
    setAdminUnseenOpenIds((prev) =>
      prev.filter((id) => !tickets.some((t) => t.id === id && t.status === 'Open'))
    );
  };

  const handleAcknowledgeResolvedAlerts = () => {
    setAdminUnseenResolvedIds((prev) =>
      prev.filter((id) => !tickets.some((t) => t.id === id && isAdminResolved(t)))
    );
  };

  const handleTicketReview = (ticketId) => {
    setAdminUnseenOpenIds((prev) => prev.filter((id) => id !== ticketId));
    setAdminUnseenResolvedIds((prev) => prev.filter((id) => id !== ticketId));
  };

  const handleMarkAdminNotificationRead = (id) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
  };

  const handleMarkAllAdminNotificationsRead = () => {
    setNotifications((prev) =>
      prev.map((n) => (n.admin ? { ...n, read: true } : n))
    );
  };

  const handleIssueWorkOrder = ({ ticketId, engineer, title, scheduled, zone }) => {
    const now = new Date().toISOString();
    const woId = workOrderIdFromTicketId(ticketId);
    const ticket = tickets.find((t) => t.id === ticketId);

    setWorkOrders((prev) => [
      {
        id: woId,
        ticketId,
        title,
        engineer,
        status: 'Assigned',
        scheduled,
        zone,
        issuedAt: now,
      },
      ...prev,
    ]);

    setTickets((prev) =>
      prev.map((t) =>
        t.id === ticketId
          ? {
              ...t,
              engineer,
              status: 'In Progress',
              workOrderId: woId,
              updates: [
                ...(t.updates || []),
                { at: now, message: 'Ticket reviewed by admin', by: 'Admin' },
                { at: now, message: `Team leader ${engineer} assigned`, by: 'Admin' },
                { at: now, message: `Trouble ticket ${ticketId} issued as work order ${woId}`, by: 'Admin' },
              ],
            }
          : t
      )
    );

    if (ticket?.client === CLIENT_ACCOUNT.name) {
      setNotifications((prev) => [
        {
          id: `N-${Date.now()}`,
          client: CLIENT_ACCOUNT.name,
          ticketId,
          title: 'Engineer assigned',
          message: `${engineer} has been assigned. ${ticketId} is now work order ${woId}.`,
          createdAt: now,
          read: false,
        },
        ...prev,
      ]);
    }

    setNotifications((prev) => [
      {
        id: `EN-${Date.now()}`,
        engineer,
        workOrderId: woId,
        ticketId,
        title: 'New work order assigned',
        message: `${woId} — ${title}. Review client details and start field work.`,
        createdAt: now,
        read: false,
      },
      ...prev,
    ]);

    setAdminUnseenOpenIds((prev) => prev.filter((id) => id !== ticketId));
    setEngineerUnseenWoIds((prev) => [woId, ...prev]);
    playAdminBeep();
  };

  const handleAcknowledgeEngineerWorkOrders = (woIds) => {
    setEngineerUnseenWoIds((prev) => prev.filter((id) => !woIds.includes(id)));
  };

  const handleMarkEngineerNotificationRead = (id) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
  };

  const handleMarkAllEngineerNotificationsRead = () => {
    setNotifications((prev) =>
      prev.map((n) =>
        n.engineer === ENGINEER_DEMO_NAME ? { ...n, read: true } : n
      )
    );
  };

  const handleUpdateWorkOrder = ({ workOrderId, status }) => {
    const now = new Date().toISOString();
    const workOrder = workOrders.find((w) => w.id === workOrderId);
    if (!workOrder) return;

    if (status === 'In Progress' && !workOrder.workConfirmed) return;
    if (status === 'Closed') return;

    setWorkOrders((prev) =>
      prev.map((w) => {
        if (w.id !== workOrderId) return w;
        const next = { ...w, status };
        if (status === 'Closed') next.completedAt = now;
        return next;
      })
    );

    if (status === 'In Progress') {
      setTickets((prev) =>
        prev.map((t) =>
          t.id === workOrder.ticketId
            ? {
                ...t,
                status: 'In Progress',
                updates: [
                  ...(t.updates || []),
                  { at: now, message: 'Field work started on site', by: workOrder.engineer },
                ],
              }
            : t
        )
      );

      const ticket = tickets.find((t) => t.id === workOrder.ticketId);
      if (ticket?.client === CLIENT_ACCOUNT.name) {
        setNotifications((prev) => [
          {
            id: `N-${Date.now()}`,
            client: CLIENT_ACCOUNT.name,
            ticketId: workOrder.ticketId,
            title: 'Work in progress',
            message: `${workOrder.engineer} has started work on ticket ${workOrder.ticketId}.`,
            createdAt: now,
            read: false,
          },
          ...prev,
        ]);
      }
    }

    if (status === 'Closed') {
      return;
    }
  };

  const handleCompleteWorkOrder = ({ workOrderId, reconciledMaterials, sitePhotos }) => {
    const now = new Date().toISOString();
    const workOrder = workOrders.find((w) => w.id === workOrderId);
    if (!workOrder || workOrder.status !== 'In Progress') return;

    const usedSummary = (reconciledMaterials || [])
      .map((m) => `${m.label}: ${m.usedQty}/${m.plannedQty}`)
      .join('; ');

    setWorkOrders((prev) =>
      prev.map((w) =>
        w.id === workOrderId
          ? {
              ...w,
              status: 'Closed',
              completedAt: now,
              materialsReconciled: true,
              reconciledMaterials,
              sitePhotos: sitePhotos || [],
            }
          : w
      )
    );

    setTickets((prev) =>
      prev.map((t) =>
        t.id === workOrder.ticketId
          ? {
              ...t,
              status: 'Resolved',
              updates: [
                ...(t.updates || []),
                {
                  at: now,
                  message: usedSummary
                    ? `Work completed — materials reconciled (${usedSummary})`
                    : 'Work completed — ticket resolved',
                  by: workOrder.engineer,
                },
                ...(sitePhotos?.length
                  ? [{ at: now, message: `${sitePhotos.length} site photo(s) uploaded`, by: workOrder.engineer }]
                  : []),
              ],
            }
          : t
      )
    );

    const ticket = tickets.find((t) => t.id === workOrder.ticketId);
    if (ticket?.client === CLIENT_ACCOUNT.name) {
      setNotifications((prev) => [
        {
          id: `N-${Date.now()}`,
          client: CLIENT_ACCOUNT.name,
          ticketId: workOrder.ticketId,
          title: 'Issue resolved',
          message: `Your ticket ${workOrder.ticketId} has been resolved. Please confirm and rate our service.`,
          createdAt: now,
          read: false,
        },
        ...prev,
      ]);
    }
  };

  const handleConfirmWorkOrder = ({ workOrderId, materialPlan, selectedTools }) => {
    const plan = (materialPlan || []).filter((m) => m?.label);
    const labels = selectedTools || plan.map((p) => formatMaterialQtyLine(p));
    if (!plan.length && !labels.length) return;

    const now = new Date().toISOString();
    const workOrder = workOrders.find((w) => w.id === workOrderId);
    if (!workOrder || workOrder.status !== 'Assigned') return;

    setWorkOrders((prev) =>
      prev.map((w) =>
        w.id === workOrderId
          ? {
              ...w,
              materialPlan: plan,
              selectedTools: labels,
              workConfirmed: true,
              workConfirmedAt: now,
            }
          : w
      )
    );

    setTickets((prev) =>
      prev.map((t) =>
        t.id === workOrder.ticketId
          ? {
              ...t,
              updates: [
                ...(t.updates || []),
                {
                  at: now,
                  message: `Engineer confirmed tools & materials: ${labels.join(', ')}`,
                  by: workOrder.engineer,
                },
              ],
            }
          : t
      )
    );
  };

  const handleSubmitDelayReason = ({ workOrderId, delayReason }) => {
    const trimmed = String(delayReason || '').trim();
    if (!trimmed) return;
    setWorkOrders((prev) =>
      prev.map((w) => (w.id === workOrderId ? { ...w, delayReason: trimmed } : w))
    );
  };

  const handleSubmitClientFeedback = ({ ticketId, serviceRating, feedbackComment }) => {
    const now = new Date().toISOString();
    const ticket = tickets.find((t) => t.id === ticketId);

    setTickets((prev) =>
      prev.map((t) =>
        t.id === ticketId
          ? {
              ...t,
              clientConfirmed: true,
              serviceRating,
              feedbackComment,
              confirmedAt: now,
              updates: [
                ...(t.updates || []),
                {
                  at: now,
                  message: `Client confirmed resolved — service rated ${serviceRating}/5`,
                  by: 'Client',
                },
              ],
            }
          : t
      )
    );

    if (ticket?.engineer) {
      setNotifications((prev) => [
        {
          id: `EN-${Date.now()}`,
          engineer: ticket.engineer,
          ticketId,
          title: 'Customer feedback received',
          message: `Client confirmed ticket ${ticketId} resolved — service rated ${serviceRating}/5.`,
          createdAt: now,
          read: false,
        },
        ...prev,
      ]);
    }

    setNotifications((prev) => [
      {
        id: `AD-${Date.now()}`,
        admin: true,
        ticketId,
        title: 'Client confirmed resolution',
        message: `${ticket?.client || 'Client'} confirmed ${ticketId} — service rated ${serviceRating}/5. Ticket moved to Resolved.`,
        createdAt: now,
        read: false,
      },
      ...prev,
    ]);

    setAdminUnseenResolvedIds((prev) => [ticketId, ...prev]);
    playAdminBeep();
  };

  return (
    <div className="flex min-h-full flex-col bg-slate-50 text-slate-900">
      <header className="border-b border-slate-200 bg-white px-4 py-4 sm:px-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-teal-600 text-white">
              <Headset className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-slate-900 sm:text-xl">Customer Services Kenya</h1>
              <p className="text-sm text-slate-500">Fiber ISP · Trouble ticket → work order workflow</p>
            </div>
          </div>
          <div
            className="inline-flex w-full flex-wrap gap-1 rounded-lg border border-slate-200 bg-slate-100 p-1 sm:w-auto"
            role="tablist"
            aria-label="Role views"
          >
            {ROLES.map((r) => {
              const config = ROLE_CONFIG[r];
              const Icon = config.icon;
              const isActive = role === r;
              return (
                <button
                  key={r}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setRole(r)}
                  className={`inline-flex flex-1 items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors sm:flex-none sm:px-4 ${
                    isActive ? config.tabActive : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <Icon className="h-4 w-4 shrink-0" aria-hidden />
                  {config.label}
                </button>
              );
            })}
          </div>
        </div>
      </header>

      <main className="flex-1 px-4 py-6 sm:px-6">
        <div role="tabpanel" aria-label={`${role} view content`}>
          {role === 'Client' ? (
            <ClientView
              tickets={tickets}
              workOrders={workOrders}
              account={CLIENT_ACCOUNT}
              notifications={notifications}
              onCreateTicket={handleCreateTicket}
              onUpdateCoordinates={handleUpdateTicketCoordinates}
              onMarkNotificationRead={handleMarkNotificationRead}
              onMarkAllNotificationsRead={handleMarkAllNotificationsRead}
              onSubmitFeedback={handleSubmitClientFeedback}
            />
          ) : null}
          {role === 'Admin' ? (
            <AdminView
              tickets={tickets}
              workOrders={workOrders}
              notifications={notifications}
              unseenOpenTicketIds={unseenOpenTicketIds}
              unseenResolvedTicketIds={unseenResolvedTicketIds}
              onAcknowledgeOpenAlerts={handleAcknowledgeOpenAlerts}
              onAcknowledgeResolvedAlerts={handleAcknowledgeResolvedAlerts}
              onTicketReview={handleTicketReview}
              onIssueWorkOrder={handleIssueWorkOrder}
              onMarkNotificationRead={handleMarkAdminNotificationRead}
              onMarkAllNotificationsRead={handleMarkAllAdminNotificationsRead}
            />
          ) : null}
          {role === 'Engineer' ? (
            <EngineerView
              workOrders={workOrders}
              tickets={tickets}
              notifications={notifications}
              engineerName={ENGINEER_DEMO_NAME}
              unseenWorkOrderIds={engineerUnseenWoIds}
              onAcknowledgeWorkOrders={handleAcknowledgeEngineerWorkOrders}
              onMarkNotificationRead={handleMarkEngineerNotificationRead}
              onMarkAllNotificationsRead={handleMarkAllEngineerNotificationsRead}
              onUpdateWorkOrder={handleUpdateWorkOrder}
              onSubmitDelayReason={handleSubmitDelayReason}
              onConfirmWork={handleConfirmWorkOrder}
              onCompleteWork={handleCompleteWorkOrder}
            />
          ) : null}
        </div>
      </main>
    </div>
  );
}
